// components/Delivery/DeliveryComponent.tsx
import React, { useState, useMemo } from "react";
import {
  Card,
  CardBody,
  Col,
  Row,
  Table,
  Button,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Collapse,
  Spinner,
  Alert,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import {
  useUpdatePurchaseOrderItemByIDMutation,
  useCreatePurchaseOrderDeliveryItemSplitMutation,
  useUpdatePurchaseOrderDeliveryItemSplitItemByIDMutation,
  useBulkUpdatePurchaseOrderDeliveryTypeMutation,
  useUpdateStockAllocationTransferStatusMutation,
  useUpdateStockAllocationDeliveryDetailsMutation,
  useUpdatePurchaseOrderReDeliveryItemByIDMutation,
  useMarkUndeliverableMutation,
  useCancelDeliveryRequestMutation,
} from "slices/InventoryManagement/inventoryManagementSlice";
import { useGenerateReportsPDFByUuidMutation } from "slices/safetyApp/safetyappSlice";
import { useGetDeliveryRequestDownloadUidsMutation } from "slices/InventoryManagement/inventoryManagementSlice";
import AuditLogModal from "./Components/AuditLogModal";
import CancelReasonModal from "../../../Components/Common/CancelReasonModal";
import { getUserId } from "helpers/jwt-token-access/auth-token-header";
import { toast } from "react-toastify";
import { usePdfDownloader } from "Components/Hooks/usePdfDownloader";
import DownloadModal from "Components/Common/DownloadModal";
import { useDeliveryDownloader } from "Components/Hooks/useDeliveryDownload";
import { IsValidPermissions } from "Routes/AuthProtected";
import ProductThumbnail from "../CommonComponents/ProductThumbnail";
import DeliveryEmailModal from "./DeliveryEmailModal";
import TransferToNewPoModal from "./TransferToNewPoModal";

// Attachment Modal Component
const AttachmentModal = ({ isOpen, toggle, attachments, title }: any) => {
  const [selectedAttachment, setSelectedAttachment] = useState<any>(null);
  const [showPreview, setShowPreview] = useState(false);

  // Group attachments by type
  const groupedAttachments = useMemo(() => {
    const groups: { [key: string]: any[] } = {};

    attachments?.forEach((attachment: any) => {
      const type = attachment.attachment_type || "other";
      if (!groups[type]) {
        groups[type] = [];
      }
      groups[type].push(attachment);
    });

    return groups;
  }, [attachments]);

  const getAttachmentTypeLabel = (type: string) => {
    const typeLabels: { [key: string]: string } = {
      delivery_proof: "Delivery Proof",
      defect_proof: "Defect Proof",
      redelivery_proof: "Redelivery Proof",
      other: "Other Attachments",
    };
    return typeLabels[type] || type.replace("_", " ").toUpperCase();
  };

  const getAttachmentTypeColor = (type: string) => {
    const typeColors: { [key: string]: string } = {
      delivery_proof: "success",
      defect_proof: "danger",
      redelivery_proof: "info",
      other: "secondary",
    };
    return typeColors[type] || "secondary";
  };

  const isImageFile = (fileName: string) => {
    const imageExtensions = [".jpg", ".jpeg", ".png", ".gif", ".bmp", ".webp"];
    return imageExtensions.some((ext) => fileName.toLowerCase().includes(ext));
  };

  const isPdfFile = (fileName: string) => {
    return fileName.toLowerCase().includes(".pdf");
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handlePreview = (attachment: any) => {
    setSelectedAttachment(attachment);
    setShowPreview(true);
  };

  return (
    <>
      <Modal isOpen={isOpen} toggle={toggle} size="lg">
        <ModalHeader toggle={toggle}>
          <i className="ri-attachment-line me-2"></i>
          {title || "Attachments"}
        </ModalHeader>
        <ModalBody>
          {Object.keys(groupedAttachments).length === 0 ? (
            <div className="text-center py-4">
              <i className="ri-inbox-line display-4 text-muted opacity-50"></i>
              <h5 className="mt-3 text-muted">No Attachments</h5>
              <p className="text-muted">
                There are no attachments for this item.
              </p>
            </div>
          ) : (
            <div className="attachments-container">
              {Object.keys(groupedAttachments).map((type) => (
                <div key={type} className="mb-4">
                  <div className="d-flex align-items-center mb-3">
                    <Badge
                      color={getAttachmentTypeColor(type)}
                      className="fs-12"
                    >
                      {getAttachmentTypeLabel(type)}
                    </Badge>
                    <span className="text-muted fs-12 ms-2">
                      ({groupedAttachments[type].length} files)
                    </span>
                  </div>

                  <div className="row g-3">
                    {groupedAttachments[type].map((attachment, index) => (
                      <div key={attachment.id || index} className="col-md-6">
                        <div className="card border">
                          <div className="card-body p-3">
                            <div className="d-flex align-items-center">
                              <div className="flex-shrink-0">
                                {isImageFile(
                                  attachment.file || attachment.file_url
                                ) ? (
                                  <div
                                    className="avatar-md bg-light rounded d-flex align-items-center justify-content-center cursor-pointer"
                                    onClick={() => handlePreview(attachment)}
                                  >
                                    <i className="ri-image-line text-primary fs-18"></i>
                                  </div>
                                ) : isPdfFile(
                                  attachment.file || attachment.file_url
                                ) ? (
                                  <div className="avatar-md bg-light rounded d-flex align-items-center justify-content-center">
                                    <i className="ri-file-pdf-line text-danger fs-18"></i>
                                  </div>
                                ) : (
                                  <div className="avatar-md bg-light rounded d-flex align-items-center justify-content-center">
                                    <i className="ri-file-line text-secondary fs-18"></i>
                                  </div>
                                )}
                              </div>
                              <div className="flex-grow-1 ms-3">
                                <h6 className="fs-13 mb-1 text-truncate">
                                  {attachment.file_name ||
                                    attachment.description ||
                                    `Attachment ${index + 1}`}
                                </h6>
                                <div className="text-muted fs-11">
                                  {formatFileSize(attachment.file_size || 0)}
                                </div>
                                {attachment.created_at && (
                                  <div className="text-muted fs-11">
                                    {new Date(
                                      attachment.created_at
                                    ).toLocaleDateString()}
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="mt-3 d-flex gap-2">
                              <Button
                                color="outline-primary"
                                size="sm"
                                className="flex-fill"
                                onClick={() =>
                                  window.open(
                                    attachment.file_url || attachment.file,
                                    "_blank"
                                  )
                                }
                              >
                                <i className="ri-download-line align-bottom me-1"></i>
                                Download
                              </Button>

                              {isImageFile(
                                attachment.file || attachment.file_url
                              ) && (
                                  <Button
                                    color="outline-info"
                                    size="sm"
                                    className="flex-fill"
                                    onClick={() => handlePreview(attachment)}
                                  >
                                    <i className="ri-eye-line align-bottom me-1"></i>
                                    Preview
                                  </Button>
                                )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggle}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Image Preview Modal */}
      <Modal
        isOpen={showPreview}
        toggle={() => setShowPreview(false)}
        size="xl"
      >
        <ModalHeader toggle={() => setShowPreview(false)}>
          Preview Attachment
        </ModalHeader>
        <ModalBody className="text-center">
          {selectedAttachment && (
            <div>
              <img
                src={selectedAttachment.file_url || selectedAttachment.file}
                alt={selectedAttachment.file_name || "Preview"}
                className="img-fluid"
                style={{ maxHeight: "70vh", objectFit: "contain" }}
              />
              <div className="mt-3">
                <h6>
                  {selectedAttachment.file_name ||
                    selectedAttachment.description}
                </h6>
                <p className="text-muted">
                  {formatFileSize(selectedAttachment.file_size || 0)}
                </p>
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="outline-primary"
            onClick={() =>
              window.open(
                selectedAttachment?.file_url || selectedAttachment?.file,
                "_blank"
              )
            }
          >
            <i className="ri-download-line align-bottom me-1"></i>
            Download
          </Button>
          <Button color="secondary" onClick={() => setShowPreview(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

// Contact Details Modal Component
const ContactDetailsModal = ({
  isOpen,
  toggle,
  item,
  onUpdateContact,
  isSplit = false,
  isLoading = false,
}: any) => {
  const [contactPhone, setContactPhone] = useState(
    item?.contact_person_phone || ""
  );

  const handleSubmit = () => {
    onUpdateContact(contactPhone);
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={toggle}>
        <i className="ri-phone-line me-2"></i>
        Update Contact Phone {isSplit && `- Split #${item.batch_number}`}
      </ModalHeader>
      <ModalBody>
        <Form>
          <FormGroup>
            <Label for="contact_person_phone">Contact Phone</Label>
            <Input
              type="text"
              id="contact_person_phone"
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              placeholder="Enter contact phone number"
              disabled={isLoading}
            />
          </FormGroup>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={isLoading}>
          Cancel
        </Button>
        <Button color="primary" onClick={handleSubmit} disabled={isLoading}>
          {isLoading ? <Spinner size="sm" className="me-2" /> : null}
          Update Phone
        </Button>
      </ModalFooter>
    </Modal>
  );
};

// Helper function to determine delivery status
const getPoSupplierQuantity = (item: any) => {
  const mode = String(item?.source_mode || "").toLowerCase();
  if (mode === "split") {
    return parseFloat(item?.supplier_allocation_quantity || "0") || 0;
  }
  if (mode === "stock") {
    return 0;
  }
  const supplierQty = parseFloat(item?.supplier_allocation_quantity || "0") || 0;
  if (supplierQty > 0) return supplierQty;
  return parseFloat(item?.ordered_quantity || item?.quantity || 0) || 0;
};

const getUpdatedDeliveryStatus = (item: any) => {
  const isSplitRow =
    Object.prototype.hasOwnProperty.call(item || {}, "delivery_quantity") ||
    Object.prototype.hasOwnProperty.call(item || {}, "batch_number");
  const orderedQuantity = isSplitRow
    ? parseFloat(item?.delivery_quantity || item?.quantity || 0) || 0
    : getPoSupplierQuantity(item);
  const acceptedQuantity = parseFloat(item.accepted_quantity || 0);
  const deliveredQuantity = parseFloat(item.delivered_quantity || 0);

  if (orderedQuantity > 0 && acceptedQuantity >= orderedQuantity) {
    return "delivered";
  } else if (
    orderedQuantity > 0 &&
    deliveredQuantity > 0 &&
    acceptedQuantity < orderedQuantity
  ) {
    return "partially_delivered";
  } else if (item.delivery_status === "partially_delivered") {
    return "partially_delivered";
  }
  return item.delivery_status || "pending";
};

// Helper function to format date for badge
const formatDateForBadge = (dateString: string) => {
  if (!dateString || dateString === "No Date Specified") return "NODATE";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "NODATE";
  return date.toLocaleDateString("en-GB").replace(/\//g, "");
};

const normalizeDeliveryDateValue = (value: any): string | null => {
  if (value === null || value === undefined) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const lowered = raw.toLowerCase();
  if (
    lowered === "undefined" ||
    lowered === "null" ||
    lowered === "no date specified"
  ) {
    return null;
  }
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;
  return raw;
};

// Delivery Item Component
const DeliveryItem = ({
  item,
  onUpdateItem,
  supplierContact,
  purchaseOrderNumber,
  groupDate,
}: any) => {
  const poOrderedQuantity = getPoSupplierQuantity(item);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [showDefectModal, setShowDefectModal] = useState(false);
  const [showRedeliveryModal, setShowRedeliveryModal] = useState(false);
  const [showRedeliveryDefectModal, setShowRedeliveryDefectModal] =
    useState(false);
  const [showEditDateModal, setShowEditDateModal] = useState(false);
  const [showUpdateRedeliveryModal, setShowUpdateRedeliveryModal] =
    useState(false);
  const [showUpdateStatusModal, setShowUpdateStatusModal] = useState(false);
  const [showAttachmentsModal, setShowAttachmentsModal] = useState(false);
  const [showRowDeliveryEmailModal, setShowRowDeliveryEmailModal] =
    useState(false);
  const [rowEmailDeliveryDate, setRowEmailDeliveryDate] = useState<
    string | null
  >(null);
  const [rowEmailItemId, setRowEmailItemId] = useState<number | null>(null);
  const [rowEmailSplitId, setRowEmailSplitId] = useState<number | null>(null);
  // When true, the row email modal targets the RE-DELIVERY (defect-flow) quantities/PDF.
  const [rowEmailIsDefect, setRowEmailIsDefect] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [currentAttachments, setCurrentAttachments] = useState<any[]>([]);
  const [currentAttachmentTitle, setCurrentAttachmentTitle] = useState("");

  // State to track which split item is being processed
  const [currentSplit, setCurrentSplit] = useState<any>(null);

  const [deliveryData, setDeliveryData] = useState({
    delivered_quantity: parseFloat(item.delivered_quantity) || 0,
    accepted_quantity: parseFloat(item.accepted_quantity) || 0,
    delivery_notes: item.delivery_notes || "",
    delivered_date:
      item.delivered_date || new Date().toISOString().split("T")[0],
    is_defect_items: false,
    no_items_defected: parseFloat(item.no_items_defected) || 0,
    attachments: [] as File[],
  });

  const [redeliveryData, setRedeliveryData] = useState({
    redelivery_quantity: parseFloat(item.no_items_defected) || 0,
    redelivery_notes: "",
    expected_delivery_date:
      item.expected_delivery_date || new Date().toISOString().split("T")[0],
  });

  const [redeliveryDefectData, setRedeliveryDefectData] = useState({
    delivered_quantity: parseFloat(item.re_delivery_quantity) || 0,
    accepted_quantity: 0,
    no_items_defected: parseFloat(item.re_delivery_quantity) || 0,
    delivery_notes: "",
    delivered_date: new Date().toISOString().split("T")[0],
    attachments: [] as File[],
    is_defect_items: true,
    is_re_delivered: false,
  });

  const [updateRedeliveryData, setUpdateRedeliveryData] = useState({
    redelivered_quantity: 0,
    redelivered_date: new Date().toISOString().split("T")[0],
    redelivery_notes: "",
    attachments: [] as File[],
    is_re_delivered: true,
  });

  const [editDateData, setEditDateData] = useState({
    po_expected_delivery_date: item.po_expected_delivery_date || "",
  });

  const [statusUpdateData, setStatusUpdateData] = useState({
    status: getUpdatedDeliveryStatus(item),
    delivery_status: getUpdatedDeliveryStatus(item),
    delivery_notes: item.delivery_notes || "",
    delivered_quantity: parseFloat(item.delivered_quantity) || 0,
    accepted_quantity: parseFloat(item.accepted_quantity) || 0,
    no_items_defected: parseFloat(item.no_items_defected) || 0,
    delivered_date:
      item.delivered_date || new Date().toISOString().split("T")[0],
    is_defect_items: item.is_defect_items || false,
    is_re_delivered: item.is_re_delivered || false,
    contact_phone: item.contact_person_phone || "",
    delivery_type: item.delivery_type || "in_preparation",
  });
  const [showStockTransferModal, setShowStockTransferModal] = useState(false);
  const [selectedStockAllocation, setSelectedStockAllocation] = useState<any>(null);
  const [stockTransferData, setStockTransferData] = useState<{
    transfer_status: "in_transit" | "received" | "partial_received" | "cancelled";
    quantity: number;
    delivery_person_contact: string;
    delivery_date: string;
    items_arrive_from: string;
  }>({
    transfer_status: "in_transit",
    quantity: 0,
    delivery_person_contact: "",
    delivery_date: "",
    items_arrive_from: "",
  });
  const [stockTransferError, setStockTransferError] = useState<string | null>(
    null
  );

  const [splitDeliveryData, setSplitDeliveryData] = useState({
    splits: [
      {
        quantity: 0,
        delivery_date: new Date().toISOString().split("T")[0],
      },
    ],
  });

  // Add audit log modal state
  const [showAuditLogModal, setShowAuditLogModal] = useState(false);
  const [currentAuditLogs, setCurrentAuditLogs] = useState<any[]>([]);
  const [currentAuditTitle, setCurrentAuditTitle] = useState("");

  const [showSplitDeliveryModal, setShowSplitDeliveryModal] = useState(false);
  const [showUndeliverableModal, setShowUndeliverableModal] = useState(false);
  const [undeliverableNotes, setUndeliverableNotes] = useState("");
  const [usedAmount, setUsedAmount] = useState<string>("");

  const [createPurchaseOrderDeliveryItemSplit, { isLoading: isSplitting }] =
    useCreatePurchaseOrderDeliveryItemSplitMutation();

  const [updatePurchaseOrderItem, { isLoading: isUpdating }] =
    useUpdatePurchaseOrderItemByIDMutation();

  const [updatePurchaseOrderDeliveryItemSplit, { isLoading: isUpdatingSplit }] =
    useUpdatePurchaseOrderDeliveryItemSplitItemByIDMutation();

  const [updatePurchaseOrderReDelivery, { isLoading: isUpdatingRedelivery }] =
    useUpdatePurchaseOrderReDeliveryItemByIDMutation();
  const [updateStockAllocationTransferStatus, { isLoading: isUpdatingStockTransfer }] =
    useUpdateStockAllocationTransferStatusMutation();

  const [markUndeliverable, { isLoading: isMarkingUndeliverable }] =
    useMarkUndeliverableMutation();

  const [cancelDeliveryRequest, { isLoading: isCancellingDR }] =
    useCancelDeliveryRequestMutation();
  const [cancelDRTarget, setCancelDRTarget] = useState<
    { split_id?: number; item_id?: number } | null
  >(null);
  const [cancelDRError, setCancelDRError] = useState<string | null>(null);

  const handleCancelDeliveryRequest = async (reason: string) => {
    if (!cancelDRTarget) return;
    setCancelDRError(null);
    try {
      const res: any = await cancelDeliveryRequest({
        ...cancelDRTarget,
        reason,
        cancelled_by_user_id: getUserId(),
      });
      if (res?.error) {
        setCancelDRError(res.error?.data?.error || "Failed to cancel delivery request.");
        return;
      }
      toast.success(res?.data?.message || "Delivery request cancelled successfully.");
      setCancelDRTarget(null);
    } catch (e) {
      setCancelDRError("Failed to cancel delivery request.");
    }
  };

  const [generateReportsPDFByUuid] = useGenerateReportsPDFByUuidMutation();
  const [getDeliveryRequestDownloadUids] =
    useGetDeliveryRequestDownloadUidsMutation();

  const handleUpdateStockTransferStatus = async (
    allocationId: number,
    transferStatus:
      | "issued"
      | "in_transit"
      | "received"
      | "partial_received"
      | "cancelled",
    quantity?: string | number,
    deliveryDetails?: {
      delivery_person_contact?: string;
      delivery_date?: string;
      items_arrive_from?: string;
    }
  ) => {
    try {
      await updateStockAllocationTransferStatus({
        id: allocationId,
        values: {
          transfer_status: transferStatus,
          ...(quantity !== undefined ? { quantity } : {}),
          ...(deliveryDetails || {}),
        },
      }).unwrap();
      onUpdateItem();
      return true;
    } catch (error: any) {
      const msg =
        error?.data?.error || "Failed to update stock transfer status.";
      setStockTransferError(msg);
      console.error("Failed to update stock transfer status", error);
      return false;
    }
  };

  const openStockTransferModal = (allocation: any) => {
    const currentStatus = String(allocation?.transfer_status || "").toLowerCase();
    const defaultStatus =
      currentStatus === "in_transit" || currentStatus === "partial_received"
        ? "received"
        : "in_transit";
    const defaultQty =
      parseFloat(
        String(
          allocation?.issued_qty ||
            allocation?.allocated_from_stock_qty ||
            0
        )
      ) || 0;
    setSelectedStockAllocation(allocation);
    setStockTransferError(null);
    setStockTransferData({
      transfer_status: defaultStatus as
        | "in_transit"
        | "received"
        | "partial_received"
        | "cancelled",
      quantity: defaultQty,
      delivery_person_contact: allocation?.delivery_person_contact || "",
      delivery_date: allocation?.delivery_date || "",
      items_arrive_from: allocation?.items_arrive_from || "",
    });
    setShowStockTransferModal(true);
  };

  const handleSubmitStockTransferUpdate = async () => {
    if (!selectedStockAllocation) return;
    const nextStatus = stockTransferData.transfer_status;
    const qty = stockTransferData.quantity;
    const isReceiveType =
      nextStatus === "received" || nextStatus === "partial_received";

    if (isReceiveType) {
      if (
        !stockTransferData.delivery_person_contact.trim() ||
        !stockTransferData.delivery_date ||
        !stockTransferData.items_arrive_from.trim()
      ) {
        setStockTransferError(
          "Please fill in the delivery details (contact, date, items arrive from) before marking as received."
        );
        return;
      }
    }
    setStockTransferError(null);

    const ok = await handleUpdateStockTransferStatus(
      selectedStockAllocation.id,
      nextStatus,
      isReceiveType ? qty : undefined,
      {
        delivery_person_contact:
          stockTransferData.delivery_person_contact || undefined,
        delivery_date: stockTransferData.delivery_date || undefined,
        items_arrive_from: stockTransferData.items_arrive_from || undefined,
      }
    );
    if (ok) {
      setShowStockTransferModal(false);
      setSelectedStockAllocation(null);
    }
  };

  const handleSplitPlanningCheckbox = async (
    splitId: number,
    checked: boolean
  ) => {
    try {
      await updatePurchaseOrderDeliveryItemSplit({
        id: splitId,
        values: {
          split_id: splitId,
          delivery_type: checked ? "planned" : "in_preparation",
        },
      }).unwrap();
      onUpdateItem();
    } catch (error) {
      console.error("Failed to update split delivery_type", error);
    }
  };

  const handleItemPlanningCheckbox = async (
    itemId: number,
    checked: boolean
  ) => {
    try {
      const formData = new FormData();
      formData.append("item_id", itemId.toString());
      formData.append("pr_id", (item.purchase_request || item.purchase_request).toString());
      formData.append("delivery_type", checked ? "planned" : "in_preparation");
      await updatePurchaseOrderItem({
        id: itemId,
        values: formData,
      }).unwrap();
      onUpdateItem();
    } catch (error) {
      console.error("Failed to update item delivery_type", error);
    }
  };

  const {
    isOpen,
    status,
    permitNo,
    errorMessage,
    message,
    handleDeliveryDownload,
    isDownloading,
    closeDownloadModal,
  } = useDeliveryDownloader({
    getDeliveryRequestDownloadUids,
    generateReportsPDFByUuid,
  });

  // Check if item has delivery records
  const hasDeliveryRecords = item.delivered_quantity > 0 || item.delivered_date;

  // Delivery request cancelled: keep it visible but only allow download
  const isDeliveryCancelled = item.delivery_status === "cancelled";

  // PO cut-off: can mark undeliverable when the item is eligible (non-material)
  // and still pending / partially delivered. This matches the backend rule
  // (mark-undeliverable allows any category except MATERIAL / M001).
  const itemCategory = (item.category_name || "").toUpperCase();
  const isUndeliverableEligibleCategory =
    itemCategory !== "" && itemCategory !== "MATERIAL" && itemCategory !== "M001";
  const canMarkUndeliverable =
    isUndeliverableEligibleCategory &&
    (item.delivery_status === "pending" ||
      item.delivery_status === "partially_delivered") &&
    !item.item_undeliverable;

  // Handle mark as undeliverable
  const handleMarkUndeliverable = async () => {
    if (!undeliverableNotes.trim()) {
      alert("Please provide a reason for marking this item as undeliverable");
      return;
    }
    const original = parseFloat(item.total_item_cost) || 0;
    const used = parseFloat(usedAmount || "0") || 0;
    if (used > original) {
      alert(`Used amount (${used}) cannot exceed original amount (${original})`);
      return;
    }
    try {
      await markUndeliverable({
        po_id: item.purchase_order,
        item_ids: [item.id],
        undeliverable_notes: undeliverableNotes,
        item_used_amounts: { [item.id]: used },
      }).unwrap();
      setShowUndeliverableModal(false);
      setUndeliverableNotes("");
      setUsedAmount("");
      onUpdateItem();
    } catch (error: any) {
      console.error("Error marking undeliverable:", error);
      alert(error?.data?.error || "Failed to mark item as undeliverable");
    }
  };

  // Calculate remaining quantity for redelivery
  const getRemainingRedeliveryQuantity = () => {
    const orderedQuantity = poOrderedQuantity;
    const acceptedQuantity = parseFloat(item.accepted_quantity || 0);
    return orderedQuantity - acceptedQuantity;
  };

  // Handle file selection
  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    field: string = "attachments"
  ) => {
    const files = event.target.files;
    if (files) {
      const newFiles = Array.from(files);
      if (field === "attachments") {
        setDeliveryData((prev) => ({
          ...prev,
          attachments: [...prev.attachments, ...newFiles],
        }));
      } else if (field === "redelivery_attachments") {
        setUpdateRedeliveryData((prev) => ({
          ...prev,
          attachments: [...prev.attachments, ...newFiles],
        }));
      } else if (field === "redelivery_defect_attachments") {
        setRedeliveryDefectData((prev) => ({
          ...prev,
          attachments: [...prev.attachments, ...newFiles],
        }));
      }
    }
  };

  // Function to show audit logs
  const handleShowAuditLogs = (auditLogs: any[], title: string) => {
    setCurrentAuditLogs(auditLogs || []);
    setCurrentAuditTitle(title);
    setShowAuditLogModal(true);
  };

  // Remove file from attachments
  const removeFile = (index: number, field: string = "attachments") => {
    if (field === "attachments") {
      setDeliveryData((prev) => ({
        ...prev,
        attachments: prev.attachments.filter((_, i) => i !== index),
      }));
    } else if (field === "redelivery_attachments") {
      setUpdateRedeliveryData((prev) => ({
        ...prev,
        attachments: prev.attachments.filter((_, i) => i !== index),
      }));
    } else if (field === "redelivery_defect_attachments") {
      setRedeliveryDefectData((prev) => ({
        ...prev,
        attachments: prev.attachments.filter((_, i) => i !== index),
      }));
    }
  };

  // Show attachments modal
  const handleShowAttachments = (attachments: any[], title: string) => {
    setCurrentAttachments(attachments);
    setCurrentAttachmentTitle(title);
    setShowAttachmentsModal(true);
  };

  // Get all attachments for an item (including split deliveries)
  const getAllAttachments = (item: any) => {
    const allAttachments = [
      ...(item.attachments || []),
      ...(item.delivery_attachments || []),
      ...(item.defect_attachments || []),
    ];

    if (item.is_split && item.split_deliveries) {
      item.split_deliveries.forEach((split: any) => {
        if (split.attachments) {
          allAttachments.push(...split.attachments);
        }
      });
    }

    return allAttachments;
  };

  // Handle contact phone update
  const handleUpdateContactPhone = async (phone: string) => {
    try {
      const formData = new FormData();
      formData.append("contact_person_phone", phone);

      if (currentSplit) {
        // For split items, include split_id
        formData.append("item_id", item.id);
        formData.append(
          "pr_id",
          item.purchase_request || item.purchase_request
        );
        formData.append("split_id", currentSplit.id);

        await updatePurchaseOrderDeliveryItemSplit({
          id: currentSplit.id,
          values: formData,
        }).unwrap();
      } else {
        // For normal items, include item_id and pr_id
        formData.append("item_id", item.id);
        formData.append(
          "pr_id",
          item.purchase_request || item.purchase_request
        );

        await updatePurchaseOrderItem({
          id: item.id,
          values: formData,
        }).unwrap();
      }

      setShowContactModal(false);
      setCurrentSplit(null);
      onUpdateItem();
    } catch (error) {
      console.error("Error updating contact phone:", error);
    }
  };

  // Download handler for delivery items
  const handleDownloadDelivery = (isDefectItems: boolean = false) => {
    const deliveryDate = normalizeDeliveryDateValue(item.po_expected_delivery_date);
    handleDeliveryDownload(
      purchaseOrderNumber,
      deliveryDate,
      isDefectItems,
      "normal",
      item.id,
      null,
      item.delivery_status === "cancelled"
    );
  };

  // Download handler for redelivery items
  const handleDownloadRedelivery = (isDefectItems: boolean = true) => {
    const redeliveryDate = item.re_delivery_date;
    handleDeliveryDownload(
      purchaseOrderNumber,
      redeliveryDate,
      isDefectItems,
      "redelivery",
      item.id,
      null
    );
  };

  // Download handler for split items
  const handleDownloadSplitDelivery = (
    split: any,
    isDefectItems: boolean = false
  ) => {
    const deliveryDate =
      normalizeDeliveryDateValue(
        split.delivery_date ||
          split.po_expected_delivery_date ||
          item.po_expected_delivery_date
      ) || null;
    handleDeliveryDownload(
      purchaseOrderNumber,
      deliveryDate,
      isDefectItems,
      "normal",
      item.id,
      split.id,
      split.delivery_status === "cancelled"
    );
  };

  // Download handler for split redelivery items
  const handleDownloadSplitRedelivery = (
    split: any,
    isDefectItems: boolean = true
  ) => {
    const redeliveryDate = split.re_delivery_date;
    handleDeliveryDownload(
      purchaseOrderNumber,
      redeliveryDate,
      isDefectItems,
      "redelivery",
      item.id,
      split.id
    );
  };

  // Split Delivery Functions
  const handleAddSplitDelivery = () => {
    setSplitDeliveryData((prev) => ({
      ...prev,
      splits: [
        ...prev.splits,
        {
          quantity: 0,
          delivery_date: new Date().toISOString().split("T")[0],
        },
      ],
    }));
  };

  const handleRemoveSplitDelivery = (index: number) => {
    if (splitDeliveryData.splits.length > 1) {
      setSplitDeliveryData((prev) => ({
        ...prev,
        splits: prev.splits.filter((_, i) => i !== index),
      }));
    }
  };

  const handleSplitDeliveryChange = (
    index: number,
    field: string,
    value: any
  ) => {
    setSplitDeliveryData((prev) => ({
      ...prev,
      splits: prev.splits.map((delivery, i) =>
        i === index ? { ...delivery, [field]: value } : delivery
      ),
    }));
  };

  const calculateTotalSplitQuantity = () => {
    return splitDeliveryData.splits.reduce(
      (total, delivery) =>
        total + (parseFloat(delivery.quantity.toString()) || 0),
      0
    );
  };

  const getRemainingQuantity = () => {
    const totalSplit = calculateTotalSplitQuantity();
    return poOrderedQuantity - totalSplit;
  };

  const handleSplitDelivery = async () => {
    try {
      const totalSplitQuantity = calculateTotalSplitQuantity();

      if (totalSplitQuantity > poOrderedQuantity) {
        alert("Total split quantity cannot exceed ordered quantity");
        return;
      }

      if (totalSplitQuantity < poOrderedQuantity) {
        alert("Total split quantity must equal ordered quantity");
        return;
      }

      const splitData = {
        item_id: item.id,
        pr_id: item.purchase_request || item.purchase_request,
        splits: splitDeliveryData.splits.map((delivery) => ({
          quantity: parseFloat(delivery.quantity.toString()),
          delivery_date: delivery.delivery_date,
        })),
      };

      await createPurchaseOrderDeliveryItemSplit(splitData).unwrap();

      setShowSplitDeliveryModal(false);
      setSplitDeliveryData({
        splits: [
          {
            quantity: 0,
            delivery_date: new Date().toISOString().split("T")[0],
          },
        ],
      });
      onUpdateItem();
    } catch (error) {
      console.error("Error splitting delivery:", error);
    }
  };

  const renderUserInfo = (item: any) => {
    return (
      <div className="mt-2">
        {item.audit_logs?.length > 0 && (
          <div className="mt-2">
            <Button
              color="outline-info"
              size="sm"
              onClick={() =>
                handleShowAuditLogs(
                  item.audit_logs,
                  `${item.product_name}${item.is_split ? ` - Split #${item.batch_number}` : ""
                  }`
                )
              }
            >
              <i className="ri-history-line align-bottom me-1"></i>
              View Audit ({item.audit_logs.length})
            </Button>
          </div>
        )}
      </div>
    );
  };

  // FIXED: Improved logic for defect states
  const hasDefects = item.no_items_defected > 0;
  const isFullyDelivered = getUpdatedDeliveryStatus(item) === "delivered";
  const hasPendingDefects =
    item.no_items_defected > 0 &&
    !item.is_re_delivered &&
    !item.re_delivery_date;
  const hasRedeliveryDefects =
    item.is_re_delivered && item.no_items_defected > 0;
  const hasArrangedRedelivery = item.re_delivery_date && !item.is_re_delivered;
  const isCompleted =
    parseFloat(item.delivered_quantity || 0) === getPoSupplierQuantity(item) &&
    parseFloat(item.accepted_quantity || 0) === getPoSupplierQuantity(item);
  const canSplitDelivery =
    !isCompleted &&
    getUpdatedDeliveryStatus(item) !== "delivered" &&
    !item.is_split &&
    !isDeliveryCancelled;

  // FIXED: Improved logic for showing redelivery arrangement options
  const canArrangeRedelivery =
    (hasPendingDefects || hasRedeliveryDefects) &&
    !hasArrangedRedelivery &&
    !isDeliveryCancelled;
  const canUpdateRedelivery = hasArrangedRedelivery;

  // Normal Item Handlers
  const handleMarkDelivered = async () => {
    try {
      if (deliveryData.attachments.length === 0) {
        alert("Please attach delivery proof");
        return;
      }

      const formData = new FormData();
      const updatedStatus = getUpdatedDeliveryStatus({
        ...item,
        accepted_quantity: deliveryData.accepted_quantity,
        quantity: item.quantity,
      });

      formData.append("item_id", item.id);
      formData.append("pr_id", item.purchase_request || item.purchase_request);
      formData.append("status", updatedStatus);
      formData.append("delivery_status", updatedStatus);
      formData.append("delivery_notes", deliveryData.delivery_notes);
      formData.append("is_defect_items", "false");
      formData.append("no_items_defected", "0");
      formData.append(
        "delivered_quantity",
        deliveryData.delivered_quantity.toString()
      );
      formData.append(
        "accepted_quantity",
        deliveryData.accepted_quantity.toString()
      );
      formData.append("delivered_date", deliveryData.delivered_date);

      deliveryData.attachments.forEach((file, index) => {
        formData.append(`attachments`, file);
        formData.append(`attachment_type`, "delivery_proof");
      });

      await updatePurchaseOrderItem({
        id: item.id,
        values: formData,
      }).unwrap();

      setShowDeliveryModal(false);
      setDeliveryData((prev) => ({ ...prev, attachments: [] }));
      onUpdateItem();
    } catch (error) {
      console.error("Error updating delivery:", error);
    }
  };

  const handleRaiseDefect = async () => {
    try {
      if (deliveryData.attachments.length === 0) {
        alert("Please attach defect proof");
        return;
      }

      const formData = new FormData();
      const updatedStatus = "partially_delivered";

      formData.append("item_id", item.id);
      formData.append("pr_id", item.purchase_request || item.purchase_request);
      formData.append("status", updatedStatus);
      formData.append("delivery_status", updatedStatus);
      formData.append("delivery_notes", deliveryData.delivery_notes);
      formData.append("is_defect_items", "true");
      formData.append(
        "no_items_defected",
        deliveryData.no_items_defected.toString()
      );
      formData.append(
        "delivered_quantity",
        deliveryData.delivered_quantity.toString()
      );
      formData.append(
        "accepted_quantity",
        deliveryData.accepted_quantity.toString()
      );
      formData.append("delivered_date", deliveryData.delivered_date);
      formData.append(
        "defect_reported_date",
        new Date().toISOString().split("T")[0]
      );
      formData.append("defect_description", deliveryData.delivery_notes);
      formData.append("is_re_delivered", "False");

      deliveryData.attachments.forEach((file, index) => {
        formData.append(`attachments`, file);
        formData.append(`attachment_type`, "defect_proof");
      });

      await updatePurchaseOrderItem({
        id: item.id,
        values: formData,
      }).unwrap();

      setShowDefectModal(false);
      setDeliveryData((prev) => ({ ...prev, attachments: [] }));
      onUpdateItem();
    } catch (error) {
      console.error("Error raising defect:", error);
    }
  };

  const handleArrangeRedelivery = async () => {
    try {
      const formData = new FormData();

      formData.append("item_id", item.id);
      formData.append("pr_id", item.purchase_request || item.purchase_request);
      formData.append(
        "re_delivery_date",
        redeliveryData.expected_delivery_date
      );
      formData.append("re_delivery_notes", redeliveryData.redelivery_notes);
      formData.append(
        "re_delivery_quantity",
        redeliveryData.redelivery_quantity.toString()
      );
      formData.append("is_re_delivered", "False");
      formData.append("delivery_status", "partially_delivered");
      formData.append("status", "partially_delivered");

      await updatePurchaseOrderItem({
        id: item.id,
        values: formData,
      }).unwrap();

      setShowRedeliveryModal(false);
      onUpdateItem();
    } catch (error) {
      console.error("Error arranging redelivery:", error);
    }
  };

  const handleUpdateRedeliveryStatus = async () => {
    try {
      if (updateRedeliveryData.attachments.length === 0) {
        alert("Please attach redelivery proof");
        return;
      }

      const formData = new FormData();
      const currentAcceptedQuantity = parseFloat(item.accepted_quantity || 0);
      const newAcceptedQuantity =
        currentAcceptedQuantity +
        parseFloat(updateRedeliveryData.redelivered_quantity.toString());
      const updatedStatus =
        newAcceptedQuantity >= poOrderedQuantity
          ? "delivered"
          : "partially_delivered";

      formData.append("item_id", item.id);
      formData.append("pr_id", item.purchase_request || item.purchase_request);
      formData.append(
        "re_delivered_date",
        updateRedeliveryData.redelivered_date
      );
      formData.append(
        "re_delivery_notes",
        updateRedeliveryData.redelivery_notes
      );
      formData.append(
        "re_delivered_quantity",
        updateRedeliveryData.redelivered_quantity.toString()
      );
      formData.append("accepted_quantity", newAcceptedQuantity.toString());
      formData.append("is_re_delivered", "True");
      formData.append("status", updatedStatus);
      formData.append("delivery_status", updatedStatus);

      updateRedeliveryData.attachments.forEach((file, index) => {
        formData.append(`attachments`, file);
        formData.append(`attachment_type`, "redelivery_proof");
      });

      await updatePurchaseOrderItem({
        id: item.id,
        values: formData,
      }).unwrap();

      setShowUpdateRedeliveryModal(false);
      setUpdateRedeliveryData((prev) => ({ ...prev, attachments: [] }));
      onUpdateItem();
    } catch (error) {
      console.error("Error updating redelivery:", error);
    }
  };

  // FIXED: Handle defect in redelivery flow with proper quantity validation
  const handleRaiseRedeliveryDefect = async () => {
    try {
      if (redeliveryDefectData.attachments.length === 0) {
        alert("Please attach defect proof for redelivery");
        return;
      }

      const formData = new FormData();

      // Calculate new accepted quantity by adding redelivery accepted to existing accepted
      const currentAcceptedQuantity = parseFloat(item.accepted_quantity || 0);
      const redeliveryAcceptedQuantity = parseFloat(
        redeliveryDefectData.accepted_quantity.toString()
      );
      const newAcceptedQuantity =
        currentAcceptedQuantity + redeliveryAcceptedQuantity;

      // Calculate new defected quantity (only new defects from this redelivery)
      const redeliveryDefectedQuantity = parseFloat(
        redeliveryDefectData.no_items_defected.toString()
      );

      // Determine status based on whether all items are accepted
      const orderedQuantity = poOrderedQuantity;
      const updatedStatus =
        newAcceptedQuantity >= orderedQuantity
          ? "delivered"
          : "partially_delivered";

      // FIXED: Proper delivered quantity calculation - don't accumulate, use the maximum
      const currentDeliveredQuantity = parseFloat(item.delivered_quantity || 0);
      const redeliveryDeliveredQuantity = parseFloat(
        redeliveryDefectData.delivered_quantity.toString()
      );

      // Use the maximum delivered quantity to avoid exceeding ordered quantity
      const newDeliveredQuantity = Math.max(
        currentDeliveredQuantity,
        redeliveryDeliveredQuantity
      );

      // Validate that delivered quantity doesn't exceed ordered quantity
      if (newDeliveredQuantity > orderedQuantity) {
        alert(
          `Delivered quantity (${newDeliveredQuantity}) cannot exceed ordered quantity (${orderedQuantity})`
        );
        return;
      }

      formData.append("item_id", item.id);
      formData.append("pr_id", item.purchase_request || item.purchase_request);
      formData.append("status", updatedStatus);
      formData.append("delivery_status", updatedStatus);
      formData.append("delivery_notes", redeliveryDefectData.delivery_notes);
      formData.append("is_defect_items", "True");

      // FIXED: Only track current defect quantity, don't accumulate
      formData.append(
        "no_items_defected",
        redeliveryDefectedQuantity.toString()
      );

      formData.append("delivered_quantity", newDeliveredQuantity.toString());
      formData.append("accepted_quantity", newAcceptedQuantity.toString());
      formData.append("delivered_date", redeliveryDefectData.delivered_date);
      formData.append(
        "defect_reported_date",
        new Date().toISOString().split("T")[0]
      );
      formData.append(
        "defect_description",
        redeliveryDefectData.delivery_notes
      );

      // Update redelivery information
      formData.append("re_delivered_date", redeliveryDefectData.delivered_date);
      formData.append(
        "re_delivered_quantity",
        redeliveryDeliveredQuantity.toString()
      );
      formData.append("re_delivery_notes", redeliveryDefectData.delivery_notes);
      formData.append("is_re_delivered", "True");

      redeliveryDefectData.attachments.forEach((file, index) => {
        formData.append(`attachments`, file);
        formData.append(`attachment_type`, "defect_proof");
      });

      await updatePurchaseOrderItem({
        id: item.id,
        values: formData,
      }).unwrap();

      setShowRedeliveryDefectModal(false);
      setRedeliveryDefectData((prev) => ({ ...prev, attachments: [] }));
      onUpdateItem();
    } catch (error: any) {
      console.error("Error raising redelivery defect:", error);
      if (error.data && error.data.detail) {
        alert(`Error: ${error.data.detail}`);
      }
    }
  };

  // Split Item Handlers
  const handleSplitItemDelivered = async () => {
    try {
      if (!currentSplit) return;

      if (deliveryData.attachments.length === 0) {
        alert("Please attach delivery proof");
        return;
      }

      const formData = new FormData();
      const updatedStatus = getUpdatedDeliveryStatus({
        ...currentSplit,
        accepted_quantity: deliveryData.accepted_quantity,
        quantity: currentSplit.delivery_quantity,
      });

      formData.append("item_id", item.id);
      formData.append("split_id", currentSplit.id);
      formData.append("pr_id", item.purchase_request || item.purchase_request);
      formData.append("status", updatedStatus);
      formData.append("delivery_status", updatedStatus);
      formData.append("delivery_notes", deliveryData.delivery_notes);
      formData.append("is_defect_items", "false");
      formData.append("no_items_defected", "0");
      formData.append(
        "delivered_quantity",
        deliveryData.delivered_quantity.toString()
      );
      formData.append(
        "accepted_quantity",
        deliveryData.accepted_quantity.toString()
      );
      formData.append("delivered_date", deliveryData.delivered_date);

      deliveryData.attachments.forEach((file, index) => {
        formData.append(`attachments`, file);
        formData.append(`attachment_type`, "delivery_proof");
      });

      await updatePurchaseOrderDeliveryItemSplit({
        id: currentSplit.id,
        values: formData,
      }).unwrap();

      setShowDeliveryModal(false);
      setDeliveryData((prev) => ({ ...prev, attachments: [] }));
      setCurrentSplit(null);
      onUpdateItem();
    } catch (error) {
      console.error("Error updating split delivery:", error);
    }
  };

  const handleSplitItemDefect = async () => {
    try {
      if (!currentSplit) return;

      if (deliveryData.attachments.length === 0) {
        alert("Please attach defect proof");
        return;
      }

      const formData = new FormData();
      const updatedStatus = "partially_delivered";

      formData.append("item_id", item.id);
      formData.append("split_id", currentSplit.id);
      formData.append("pr_id", item.purchase_request || item.purchase_request);
      formData.append("status", updatedStatus);
      formData.append("delivery_status", updatedStatus);
      formData.append("delivery_notes", deliveryData.delivery_notes);
      formData.append("is_defect_items", "true");
      formData.append(
        "no_items_defected",
        deliveryData.no_items_defected.toString()
      );
      formData.append(
        "delivered_quantity",
        deliveryData.delivered_quantity.toString()
      );
      formData.append(
        "accepted_quantity",
        deliveryData.accepted_quantity.toString()
      );
      formData.append("delivered_date", deliveryData.delivered_date);
      formData.append(
        "defect_reported_date",
        new Date().toISOString().split("T")[0]
      );
      formData.append("defect_description", deliveryData.delivery_notes);
      formData.append("is_re_delivered", "False");

      deliveryData.attachments.forEach((file, index) => {
        formData.append(`attachments`, file);
        formData.append(`attachment_type`, "defect_proof");
      });

      await updatePurchaseOrderDeliveryItemSplit({
        id: currentSplit.id,
        values: formData,
      }).unwrap();

      setShowDefectModal(false);
      setDeliveryData((prev) => ({ ...prev, attachments: [] }));
      setCurrentSplit(null);
      onUpdateItem();
    } catch (error) {
      console.error("Error raising split defect:", error);
    }
  };

  const handleSplitItemRedelivery = async () => {
    try {
      if (!currentSplit) return;

      const formData = new FormData();

      formData.append("item_id", item.id);
      formData.append("split_id", currentSplit.id);
      formData.append("pr_id", item.purchase_request || item.purchase_request);
      formData.append(
        "re_delivery_date",
        redeliveryData.expected_delivery_date
      );
      formData.append("re_delivery_notes", redeliveryData.redelivery_notes);
      formData.append(
        "re_delivery_quantity",
        redeliveryData.redelivery_quantity.toString()
      );
      formData.append("is_re_delivered", "False");
      formData.append("delivery_status", "partially_delivered");
      formData.append("status", "partially_delivered");

      await updatePurchaseOrderReDelivery({
        id: currentSplit.id,
        values: formData,
      }).unwrap();

      setShowRedeliveryModal(false);
      setCurrentSplit(null);
      onUpdateItem();
    } catch (error) {
      console.error("Error arranging split redelivery:", error);
    }
  };

  const handleSplitItemUpdateRedeliveryStatus = async () => {
    try {
      if (!currentSplit) return;

      if (updateRedeliveryData.attachments.length === 0) {
        alert("Please attach redelivery proof");
        return;
      }

      const formData = new FormData();
      const currentAcceptedQuantity = parseFloat(
        currentSplit.accepted_quantity || 0
      );
      const newAcceptedQuantity =
        currentAcceptedQuantity +
        parseFloat(updateRedeliveryData.redelivered_quantity.toString());
      const updatedStatus =
        newAcceptedQuantity >= parseFloat(currentSplit.delivery_quantity)
          ? "delivered"
          : "partially_delivered";

      formData.append("item_id", item.id);
      formData.append("split_id", currentSplit.id);
      formData.append("pr_id", item.purchase_request || item.purchase_request);
      formData.append(
        "re_delivered_date",
        updateRedeliveryData.redelivered_date
      );
      formData.append(
        "re_delivery_notes",
        updateRedeliveryData.redelivery_notes
      );
      formData.append(
        "re_delivered_quantity",
        updateRedeliveryData.redelivered_quantity.toString()
      );
      formData.append("accepted_quantity", newAcceptedQuantity.toString());
      // Once redelivery is successfully updated, clear defect flags/count
      // so the UI no longer treats this split as having outstanding defects.
      formData.append("no_items_defected", "0");
      formData.append("is_defect_items", "False");
      formData.append("is_re_delivered", "True");
      formData.append("status", updatedStatus);
      formData.append("delivery_status", updatedStatus);

      updateRedeliveryData.attachments.forEach((file, index) => {
        formData.append(`attachments`, file);
        formData.append(`attachment_type`, "redelivery_proof");
      });

      await updatePurchaseOrderReDelivery({
        id: currentSplit.id,
        values: formData,
      }).unwrap();

      setShowUpdateRedeliveryModal(false);
      setUpdateRedeliveryData((prev) => ({ ...prev, attachments: [] }));
      setCurrentSplit(null);
      onUpdateItem();
    } catch (error) {
      console.error("Error updating split redelivery:", error);
    }
  };

  // FIXED: Handle defect in split redelivery flow with proper quantity validation
  const handleSplitItemRedeliveryDefect = async () => {
    try {
      if (!currentSplit) return;

      if (redeliveryDefectData.attachments.length === 0) {
        alert("Please attach defect proof for redelivery");
        return;
      }

      const formData = new FormData();

      // Calculate new accepted quantity by adding redelivery accepted to existing accepted
      const currentAcceptedQuantity = parseFloat(
        currentSplit.accepted_quantity || 0
      );
      const redeliveryAcceptedQuantity = parseFloat(
        redeliveryDefectData.accepted_quantity.toString()
      );
      const newAcceptedQuantity =
        currentAcceptedQuantity + redeliveryAcceptedQuantity;

      // Calculate new defected quantity (only new defects from this redelivery)
      const redeliveryDefectedQuantity = parseFloat(
        redeliveryDefectData.no_items_defected.toString()
      );

      // Determine status based on whether all items are accepted
      const orderedQuantity = parseFloat(currentSplit.delivery_quantity);
      const updatedStatus =
        newAcceptedQuantity >= orderedQuantity
          ? "delivered"
          : "partially_delivered";

      // FIXED: Proper delivered quantity calculation - don't accumulate, use the maximum
      const currentDeliveredQuantity = parseFloat(
        currentSplit.delivered_quantity || 0
      );
      const redeliveryDeliveredQuantity = parseFloat(
        redeliveryDefectData.delivered_quantity.toString()
      );

      // Use the maximum delivered quantity to avoid exceeding ordered quantity
      const newDeliveredQuantity = Math.max(
        currentDeliveredQuantity,
        redeliveryDeliveredQuantity
      );

      // Validate that delivered quantity doesn't exceed ordered quantity
      if (newDeliveredQuantity > orderedQuantity) {
        alert(
          `Delivered quantity (${newDeliveredQuantity}) cannot exceed ordered quantity (${orderedQuantity})`
        );
        return;
      }

      formData.append("item_id", item.id);
      formData.append("split_id", currentSplit.id);
      formData.append("pr_id", item.purchase_request || item.purchase_request);
      formData.append("status", updatedStatus);
      formData.append("delivery_status", updatedStatus);
      formData.append("delivery_notes", redeliveryDefectData.delivery_notes);
      formData.append("is_defect_items", "True");

      // FIXED: Only track current defect quantity, don't accumulate
      formData.append(
        "no_items_defected",
        redeliveryDefectedQuantity.toString()
      );

      formData.append("delivered_quantity", newDeliveredQuantity.toString());
      formData.append("accepted_quantity", newAcceptedQuantity.toString());
      formData.append("delivered_date", redeliveryDefectData.delivered_date);
      formData.append(
        "defect_reported_date",
        new Date().toISOString().split("T")[0]
      );
      formData.append(
        "defect_description",
        redeliveryDefectData.delivery_notes
      );

      // Update redelivery information
      formData.append("re_delivered_date", redeliveryDefectData.delivered_date);
      formData.append(
        "re_delivered_quantity",
        redeliveryDeliveredQuantity.toString()
      );
      formData.append("re_delivery_notes", redeliveryDefectData.delivery_notes);
      formData.append("is_re_delivered", "True");

      redeliveryDefectData.attachments.forEach((file, index) => {
        formData.append(`attachments`, file);
        formData.append(`attachment_type`, "defect_proof");
      });

      await updatePurchaseOrderReDelivery({
        id: currentSplit.id,
        values: formData,
      }).unwrap();

      setShowRedeliveryDefectModal(false);
      setRedeliveryDefectData((prev) => ({ ...prev, attachments: [] }));
      setCurrentSplit(null);
      onUpdateItem();
    } catch (error: any) {
      console.error("Error raising split redelivery defect:", error);
      if (error.data && error.data.detail) {
        alert(`Error: ${error.data.detail}`);
      }
    }
  };

  const handleEditExpectedDate = async () => {
    try {
      const formData = new FormData();

      formData.append("item_id", item.id);
      formData.append("pr_id", item.purchase_request || item.purchase_request);
      formData.append(
        "po_expected_delivery_date",
        editDateData.po_expected_delivery_date
      );

      await updatePurchaseOrderItem({
        id: item.id,
        values: formData,
      }).unwrap();

      setShowEditDateModal(false);
      onUpdateItem();
    } catch (error) {
      console.error("Error updating expected date:", error);
    }
  };

  // Update Status Modal with Split Delivery Support using the correct API
  const handleUpdateStatus = async () => {
    try {
      const formData = new FormData();

      const updatedStatus = statusUpdateData.status;
      const updatedDeliveryStatus = statusUpdateData.delivery_status;

      // Add common fields
      formData.append("item_id", item.id);
      formData.append("pr_id", item.purchase_request || item.purchase_request);
      formData.append("status", updatedStatus);
      formData.append("delivery_status", updatedDeliveryStatus);
      formData.append("delivery_notes", statusUpdateData.delivery_notes);
      formData.append(
        "delivered_quantity",
        statusUpdateData.delivered_quantity.toString()
      );
      formData.append(
        "accepted_quantity",
        statusUpdateData.accepted_quantity.toString()
      );
      formData.append(
        "no_items_defected",
        statusUpdateData.no_items_defected.toString()
      );
      formData.append("delivered_date", statusUpdateData.delivered_date);
      formData.append(
        "is_defect_items",
        statusUpdateData.is_defect_items ? "True" : "False"
      );
      formData.append(
        "is_re_delivered",
        statusUpdateData.is_re_delivered ? "True" : "False"
      );

      // Add contact phone if provided
      if (statusUpdateData.contact_phone) {
        formData.append("contact_person_phone", statusUpdateData.contact_phone);
      }

      // Add attachments if any
      deliveryData.attachments.forEach((file, index) => {
        formData.append(`attachments`, file);
        formData.append(`attachment_type`, "delivery_proof");
      });

      // For split items, use the split-specific API
      if (currentSplit) {
        formData.append("split_id", currentSplit.id);

        // Use the correct split update API
        await updatePurchaseOrderDeliveryItemSplit({
          id: currentSplit.id, // Use split ID for the API call
          values: formData,
        }).unwrap();
      } else {
        // For normal items, use the regular item update API
        await updatePurchaseOrderItem({
          id: item.id,
          values: formData,
        }).unwrap();
      }

      setShowUpdateStatusModal(false);
      setCurrentSplit(null);
      setDeliveryData((prev) => ({ ...prev, attachments: [] }));
      onUpdateItem();
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  const getDeliveryStatusBadge = (status: string) => {
    const statusConfig: { [key: string]: { color: string; label: string } } = {
      pending: { color: "warning", label: "Pending" },
      in_progress: { color: "info", label: "In Progress" },
      partially_delivered: { color: "primary", label: "Partially Delivered" },
      delivered: { color: "success", label: "Delivered" },
      defected: { color: "danger", label: "Defected" },
      redelivery_arranged: { color: "info", label: "Redelivery Arranged" },
    };

    const config = statusConfig[status] || {
      color: "secondary",
      label: status,
    };
    return <Badge color={config.color}>{config.label}</Badge>;
  };

  // Determine which handler to use based on whether we're processing a split item
  const getDeliveryHandler = () => {
    return currentSplit ? handleSplitItemDelivered : handleMarkDelivered;
  };

  const getDefectHandler = () => {
    return currentSplit ? handleSplitItemDefect : handleRaiseDefect;
  };

  const getRedeliveryHandler = () => {
    return currentSplit ? handleSplitItemRedelivery : handleArrangeRedelivery;
  };

  const getUpdateRedeliveryHandler = () => {
    return currentSplit
      ? handleSplitItemUpdateRedeliveryStatus
      : handleUpdateRedeliveryStatus;
  };

  const getRedeliveryDefectHandler = () => {
    return currentSplit
      ? handleSplitItemRedeliveryDefect
      : handleRaiseRedeliveryDefect;
  };

  // Render split deliveries if item is split
  const renderSplitDeliveries = () => {
    if (
      !item.is_split ||
      !item.split_deliveries ||
      item.split_deliveries.length === 0
    ) {
      return null;
    }

    const groupDateValue = normalizeDeliveryDateValue(groupDate);
    const effectiveSplits = item.split_deliveries.filter((split: any) => {
      const splitDate = normalizeDeliveryDateValue(
        split?.delivery_date || split?.po_expected_delivery_date
      );
      if (groupDate === "No Date Specified") {
        return !splitDate;
      }
      if (!groupDateValue) return true;
      return splitDate === groupDateValue;
    });

    if (!effectiveSplits.length) {
      return null;
    }

    return effectiveSplits.map((split: any) => {
      // FIXED: Apply same improved logic for split items
      const splitHasDefects = split.no_items_defected > 0;
      const splitHasPendingDefects =
        split.no_items_defected > 0 &&
        !split.is_re_delivered &&
        !split.re_delivery_date;
      const splitHasRedeliveryDefects =
        split.is_re_delivered && split.no_items_defected > 0;
      const splitHasArrangedRedelivery =
        split.re_delivery_date && !split.is_re_delivered;
      const splitIsCompleted = getUpdatedDeliveryStatus(split) === "delivered";
      const splitHasDeliveryRecords =
        split.delivered_quantity > 0 || split.delivered_date;

      // FIXED: Improved logic for split redelivery arrangement
      const splitCanArrangeRedelivery =
        (splitHasPendingDefects || splitHasRedeliveryDefects) &&
        !splitHasArrangedRedelivery;
      const splitCanUpdateRedelivery = splitHasArrangedRedelivery;

      const isInPreparation =
        split.delivery_type === "in_preparation" ||
        !split.delivery_type; // default to in_preparation if missing
      return (
        <React.Fragment key={split.id}>
          {/* Main Split Delivery Row */}
          <tr
            className={`${splitHasDefects ? "border-danger border-2" : ""} ${splitHasArrangedRedelivery ? "border-info border-2" : ""
              } ${splitIsCompleted ? "border-success border-2" : ""}`}
          >
            <td className="text-center">
              {isInPreparation ? (
                <div className="d-flex flex-column align-items-center">
                  {!splitIsCompleted && (
                    <div className="form-check mb-1">
                      <Input
                        type="checkbox"
                        className="form-check-input"
                        checked={!isInPreparation}
                        onChange={(e) =>
                          handleSplitPlanningCheckbox(split.id, e.target.checked)
                        }
                      />
                    </div>
                  )}
                  {splitIsCompleted && (
                    <span className="badge bg-soft-success text-success mb-1">
                      Delivered
                    </span>
                  )}
                  <span className="text-warning fw-semibold fs-12">
                    In preparation
                  </span>
                  <span className="text-muted fs-11 text-center">
                    This delivery is in preparation and not planned yet.
                  </span>
                </div>
              ) : (
                <div className="d-flex flex-column align-items-center">
                  {!splitIsCompleted && (
                    <div className="form-check mb-1">
                      <Input
                        type="checkbox"
                        className="form-check-input"
                        checked={!isInPreparation}
                        onChange={(e) =>
                          handleSplitPlanningCheckbox(split.id, e.target.checked)
                        }
                      />
                    </div>
                  )}
                  <span className="badge bg-soft-success text-success">
                    {splitIsCompleted ? "Delivered" : "Planned"}
                  </span>
                </div>
              )}
            </td>
            <td>
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0 avatar-md bg-light rounded p-1">
                  <ProductThumbnail
              attachment_urls={item?.attachment_urls}
              alt={item?.product_name || ''}
              size="lg"
              className="me-2"
            />
                  {/* {item.thumbnail_url ? (
                    <img
                      src={item.thumbnail_url}
                      alt={item.product_name}
                      className="img-fluid d-block rounded"
                      style={{
                        width: "60px",
                        height: "60px",
                        objectFit: "cover",
                      }}
                    />
                  ) : null}
                  {!item.thumbnail_url && (
                    <div className="avatar-title bg-soft-primary text-primary rounded w-100 h-100 d-flex align-items-center justify-content-center">
                      <i className="ri-box-3-line fs-18"></i>
                    </div>
                  )} */}
                </div>
                <div className="flex-grow-1 ms-3">
                  <h6 className="fs-14 mb-1">
                    <span className="text-dark">{item.product_name}</span>
                    <Badge color="info" className="ms-2 fs-11">
                      Split #{split.batch_number}
                    </Badge>
                    {split.delivery_status === "cancelled" && (
                      <Badge color="danger" className="ms-2 fs-11">
                        Cancelled
                      </Badge>
                    )}
                  </h6>
                  <div className="text-muted fs-12">
                    Product ID: {item.product} • Item ID: {item.id} • Split ID:{" "}
                    {split.id}
                  </div>
                  <div className="mt-2">
                    {!["cancelled", "delivered"].includes(
                      getUpdatedDeliveryStatus(split)
                    ) &&
                      IsValidPermissions(["DELETE_PURCHASE_DELIVERY"]) && (
                      <Button
                        color="danger"
                        size="sm"
                        className="me-2"
                        onClick={() => {
                          setCancelDRError(null);
                          setCancelDRTarget({ split_id: split.id });
                        }}
                      >
                        <i className="ri-close-circle-line align-bottom me-1"></i>
                        Cancel Delivery Request
                      </Button>
                    )}
                    {/* Audit Log Button for Split */}
                    {split.audit_logs?.length > 0 && (
                      <div className="mt-2">
                        <Button
                          color="outline-info"
                          size="sm"
                          onClick={() =>
                            handleShowAuditLogs(
                              split.audit_logs,
                              `${item.product_name} - Split #${split.batch_number}`
                            )
                          }
                        >
                          <i className="ri-history-line align-bottom me-1"></i>
                          View Audit ({split.audit_logs.length})
                        </Button>
                      </div>
                    )}
                    {/* Submitted By Information for Split */}
                  </div>
                  {split.attachments?.length > 0 && (
                    <div className="mt-2">
                      <Button
                        color="outline-primary"
                        size="sm"
                        onClick={() =>
                          handleShowAttachments(
                            split.attachments,
                            `Split #${split.batch_number} - ${item.product_name}`
                          )
                        }
                      >
                        <i className="ri-attachment-line align-bottom me-1"></i>
                        View Attachments ({split.attachments.length})
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </td>
            <td className="text-center">
              <span className="fw-bold">{split.delivery_quantity}</span>
              {item.measurement_name && (
                <div className="text-muted fs-12">{item.measurement_name}</div>
              )}
            </td>
            <td className="text-center">
              {split.delivered_quantity !== undefined ? (
                <div>
                  <span className="fw-bold text-success">
                    {split.delivered_quantity}
                  </span>
                  {item.measurement_name && (
                    <div className="text-muted fs-12">
                      {item.measurement_name}
                    </div>
                  )}
                </div>
              ) : (
                <span className="text-muted">-</span>
              )}
            </td>
            <td className="text-center">
              {split.accepted_quantity !== undefined ? (
                <div>
                  <span className="fw-bold text-success">
                    {split.accepted_quantity}
                  </span>
                  {item.measurement_name && (
                    <div className="text-muted fs-12">
                      {item.measurement_name}
                    </div>
                  )}
                </div>
              ) : (
                <span className="text-muted">-</span>
              )}
            </td>
            <td className="text-center">
              {split.no_items_defected !== undefined ? (
                <div>
                  <span
                    className={`fw-bold ${split.no_items_defected > 0
                        ? "text-danger"
                        : "text-success"
                      }`}
                  >
                    {split.no_items_defected}
                  </span>
                  {item.measurement_name && (
                    <div className="text-muted fs-12">
                      {item.measurement_name}
                    </div>
                  )}
                </div>
              ) : (
                <span className="text-muted">-</span>
              )}
            </td>
            <td className="text-center">
              <div className="d-flex align-items-center justify-content-center">
                {split.delivery_date ? (
                  <span className="badge bg-soft-info text-info me-2">
                    {new Date(split.delivery_date).toLocaleDateString()}
                  </span>
                ) : (
                  <span className="text-muted me-2">
                    {split?.delivery_date}
                  </span>
                )}
                {/* <Button
                  color="outline-primary"
                  size="sm"
                  className="p-1"
                  onClick={() => {
                    setEditDateData({
                      po_expected_delivery_date:
                        split.po_expected_delivery_date || "",
                    });
                    setShowEditDateModal(true);
                  }}
                  disabled={isUpdating || splitHasDeliveryRecords}
                >
                  <i className="ri-edit-line fs-12"></i>
                </Button> */}
              </div>
            </td>
            <td className="text-center">
              {split.delivery_status ? (
                getDeliveryStatusBadge(getUpdatedDeliveryStatus(split))
              ) : (
                <Badge color="warning">Pending</Badge>
              )}
            </td>
            <td className="text-center">
              {split.delivered_date ? (
                <span className="badge bg-soft-success text-success">
                  {new Date(split.delivered_date).toLocaleDateString()}
                </span>
              ) : (
                <span className="text-muted">-</span>
              )}
            </td>
            <td className="text-center">
              <div className="d-flex align-items-center justify-content-center">
                {split.contact_person_phone ? (
                  <span className="badge bg-soft-info text-info me-2">
                    {split.contact_person_phone}
                  </span>
                ) : (
                  <span className="text-muted me-2">-</span>
                )}
                {IsValidPermissions(["ADD_PURCHASE_DELIVERY"]) &&
                  <Button
                    color="outline-secondary"
                    size="sm"
                    disabled={getUpdatedDeliveryStatus(split) === "delivered"}
                    onClick={() => {
                      setCurrentSplit(split);
                      setShowContactModal(true);
                    }}
                    className="w-100"
                  >
                    <i className="ri-phone-line align-bottom me-1"></i>
                  </Button>
                }
              </div>
            </td>
            <td className="text-center">
              <div className="d-flex flex-column gap-1">
                {/* Download Button for Split Delivery */}
                {IsValidPermissions(["DOWNLOAD_PURCHASE_DELIVERY"]) &&
                  <Button
                    color="outline-success"
                    size="sm"
                    onClick={() => handleDownloadSplitDelivery(split, false)}
                    className="w-100"
                  >
                    <i className="ri-download-line align-bottom me-1"></i>
                    Download
                  </Button>
                }
                {isInPreparation && split.delivery_status !== "cancelled" && (
                  <Button
                    color="outline-primary"
                    size="sm"
                    onClick={() => {
                      setRowEmailItemId(item.id);
                      setRowEmailSplitId(split.id);
                      setRowEmailDeliveryDate(
                        normalizeDeliveryDateValue(
                          split.delivery_date ||
                            split.po_expected_delivery_date ||
                            item.po_expected_delivery_date
                        )
                      );
                      setRowEmailIsDefect(false);
                      setShowRowDeliveryEmailModal(true);
                    }}
                    className="w-100"
                  >
                    <i className="ri-mail-send-line align-bottom me-1"></i>
                    Send Email
                  </Button>
                )}

                {/* Cancelled DR: send the cancellation email manually (attaches the cancelled PDF) */}
                {split.delivery_status === "cancelled" && (
                  <Button
                    color="outline-danger"
                    size="sm"
                    onClick={() => {
                      setRowEmailItemId(item.id);
                      setRowEmailSplitId(split.id);
                      setRowEmailDeliveryDate(
                        normalizeDeliveryDateValue(
                          split.delivery_date ||
                            split.po_expected_delivery_date ||
                            item.po_expected_delivery_date
                        )
                      );
                      setShowRowDeliveryEmailModal(true);
                    }}
                    className="w-100"
                  >
                    <i className="ri-mail-send-line align-bottom me-1"></i>
                    Send Cancellation Email
                  </Button>
                )}

                {/* Update button (hidden when the delivery request is cancelled) */}
                {IsValidPermissions(["EDIT_PURCHASE_DELIVERY"]) && split.delivery_status !== "cancelled" &&
                  <Button
                    color="outline-primary"
                    size="sm"
                    onClick={() => {
                      setCurrentSplit(split);
                      setStatusUpdateData({
                        status: getUpdatedDeliveryStatus(split),
                        delivery_status: getUpdatedDeliveryStatus(split),
                        delivery_notes: split.delivery_notes || "",
                        delivered_quantity:
                          parseFloat(split.delivered_quantity) || 0,
                        accepted_quantity:
                          parseFloat(split.accepted_quantity) || 0,
                        no_items_defected:
                          parseFloat(split.no_items_defected) || 0,
                        delivered_date:
                          split.delivered_date ||
                          new Date().toISOString().split("T")[0],
                        is_defect_items: split.is_defect_items || false,
                        is_re_delivered: split.is_re_delivered || false,
                        contact_phone: split.contact_person_phone || "",
                        delivery_type: split.delivery_type || "in_preparation",
                      });
                      setDeliveryData((prev) => ({ ...prev, attachments: [] })); // Clear previous attachments
                      setShowUpdateStatusModal(true);
                    }}
                    disabled={isUpdatingSplit}
                  >
                    <i className="ri-edit-line align-bottom me-1"></i>
                    Update Split
                  </Button>
                }
                {!split.is_re_delivered &&
                  split.delivery_status !== "cancelled" &&
                  getUpdatedDeliveryStatus(split) !== "delivered" && (
                    <>
                      {/* FIXED: Show Arrange Redelivery button when there are defects but no redelivery arranged yet */}
                      {splitCanArrangeRedelivery && (
                        <>
                          {IsValidPermissions(["ADD_PURCHASE_DELIVERY"]) &&
                            <Button
                              color="info"
                              size="sm"
                              onClick={() => {
                                setCurrentSplit(split);
                                setRedeliveryData({
                                  redelivery_quantity: split.no_items_defected || 0,
                                  redelivery_notes: "",
                                  expected_delivery_date: new Date()
                                    .toISOString()
                                    .split("T")[0],
                                });
                                setShowRedeliveryModal(true);
                              }}
                              disabled={isUpdatingSplit}
                            >
                              <i className="ri-refresh-line align-bottom me-1"></i>
                              Arrange Redelivery
                            </Button>
                          }
                        </>
                      )}

                      {/* Show normal delivery buttons only if no defects or redelivery arranged */}
                      {!splitHasDefects && !splitHasArrangedRedelivery && (
                        <>
                          {IsValidPermissions(["ADD_PURCHASE_DELIVERY"]) &&
                            <Button
                              color="success"
                              size="sm"
                              onClick={() => {
                                setCurrentSplit(split);
                                setDeliveryData({
                                  delivered_quantity: split.delivery_quantity,
                                  accepted_quantity: split.delivery_quantity,
                                  delivery_notes: "",
                                  delivered_date: new Date()
                                    .toISOString()
                                    .split("T")[0],
                                  is_defect_items: false,
                                  no_items_defected: 0,
                                  attachments: [],
                                });
                                setShowDeliveryModal(true);
                              }}
                              disabled={isUpdatingSplit}
                            >
                              <i className="ri-check-line align-bottom me-1"></i>
                              Recivied
                            </Button>
                          }
                          {IsValidPermissions(["ADD_PURCHASE_DELIVERY"]) &&
                            <Button
                              color="warning"
                              size="sm"
                              onClick={() => {
                                setCurrentSplit(split);
                                setDeliveryData({
                                  delivered_quantity: split.delivery_quantity,
                                  accepted_quantity: 0,
                                  delivery_notes: "",
                                  delivered_date: new Date()
                                    .toISOString()
                                    .split("T")[0],
                                  is_defect_items: false,
                                  no_items_defected: split.delivery_quantity,
                                  attachments: [],
                                });
                                setShowDefectModal(true);
                              }}
                              disabled={isUpdatingSplit}
                            >
                              <i className="ri-error-warning-line align-bottom me-1"></i>
                              Defect
                            </Button>
                          }
                        </>
                      )}
                    </>
                  )}

                {/* Show redelivered badge only when redelivery is completed */}
                {split.is_re_delivered && (
                  <Badge color="success" className="fs-12">
                    <i className="ri-check-double-line align-middle me-1"></i>
                    Redelivered
                  </Badge>
                )}
              </div>
            </td>
          </tr>

          {/* Defect Information Row for Split Delivery */}
          {(splitHasPendingDefects || splitHasRedeliveryDefects) &&
            getUpdatedDeliveryStatus(split) === "partially_delivered" && (
              <tr className="bg-soft-danger border-danger border-start border-end border-bottom">
                <td colSpan={3} className="p-3 border-0">
                  <div className="d-flex align-items-center">
                    <Badge color="danger" className="me-2">
                      <i className="ri-error-warning-line align-middle me-1"></i>
                      {splitHasRedeliveryDefects
                        ? "Redelivery Defect Details"
                        : "Defect Details"}
                    </Badge>
                    <div className="text-muted fs-13">
                      <strong>Defected Quantity:</strong>{" "}
                      {split.no_items_defected} {item.measurement_name}
                    </div>
                  </div>
                </td>
                <td colSpan={4} className="p-3 border-0">
                  {split.defect_description && (
                    <div className="text-muted fs-13">
                      <strong>Defect Notes:</strong> {split.defect_description}
                    </div>
                  )}
                  {split.defect_reported_date && (
                    <div className="text-muted fs-13">
                      <strong>Reported Date:</strong>{" "}
                      {new Date(
                        split.defect_reported_date
                      ).toLocaleDateString()}
                    </div>
                  )}
                  {splitHasRedeliveryDefects && (
                    <div className="text-muted fs-13">
                      <strong>Cycle:</strong> Redelivery with defects - arrange
                      new redelivery
                    </div>
                  )}
                </td>
                <td colSpan={3} className="p-3 border-0 text-end">
                  {/* Download Button for Split Redelivery */}
                  {/* {IsValidPermissions(["DOWNLOAD_PURCHASE_DELIVERY"]) &&
                    <Button
                      color="outline-success"
                      size="sm"
                      onClick={() => handleDownloadSplitRedelivery(split, true)}
                      className="me-2"
                    >
                      <i className="ri-download-line align-bottom me-1"></i>
                      Download Redelivery
                    </Button>
                  } */}

                  {/* FIXED: Show Arrange Redelivery button for both pending and redelivery defects */}
                  {(splitHasPendingDefects || splitHasRedeliveryDefects) && (
                    <>
                      {IsValidPermissions(["ADD_PURCHASE_DELIVERY"]) &&
                        <Button
                          color="info"
                          size="sm"
                          onClick={() => {
                            setCurrentSplit(split);
                            setRedeliveryData({
                              redelivery_quantity: split.no_items_defected || 0,
                              redelivery_notes: "",
                              expected_delivery_date: new Date()
                                .toISOString()
                                .split("T")[0],
                            });
                            setShowRedeliveryModal(true);
                          }}
                          disabled={isUpdatingSplit}
                        >
                          <i className="ri-refresh-line align-bottom me-1"></i>
                          {splitHasRedeliveryDefects
                            ? "Arrange New Redelivery"
                            : "Arrange Redelivery"}
                        </Button>
                      }
                    </>
                  )}
                </td>
              </tr>
            )}

          {/* Redelivery Information Row for Split Delivery */}
          {splitHasArrangedRedelivery &&
            !split.is_re_delivered &&
            getUpdatedDeliveryStatus(split) === "partially_delivered" && (
              <tr className="bg-soft-info border-info border-start border-end border-bottom">
                <td colSpan={3} className="p-3 border-0">
                  <div className="d-flex align-items-center">
                    <Badge color="info" className="me-2">
                      <i className="ri-refresh-line align-middle me-1"></i>
                      Redelivery Arranged
                    </Badge>
                    <div className="text-muted fs-13">
                      <strong>Redelivery Quantity:</strong>{" "}
                      {split.re_delivery_quantity} {item.measurement_name}
                    </div>
                  </div>
                </td>
                <td colSpan={4} className="p-3 border-0">
                  {split.re_delivery_notes && (
                    <div className="text-muted fs-13">
                      <strong>Redelivery Notes:</strong>{" "}
                      {split.re_delivery_notes}
                    </div>
                  )}
                  {split.re_delivery_date && (
                    <div className="text-muted fs-13">
                      <strong>Scheduled Date:</strong>{" "}
                      {new Date(split.re_delivery_date).toLocaleDateString()}
                    </div>
                  )}
                </td>
                <td colSpan={3} className="p-3 border-0 text-end">
                  <div className="d-flex gap-2 justify-content-end">
                    {/* Download Button for Split Redelivery Arranged */}
                    {IsValidPermissions(["DOWNLOAD_PURCHASE_DELIVERY"]) &&
                      <Button
                        color="outline-success"
                        size="sm"
                        onClick={() => handleDownloadSplitRedelivery(split, true)}
                      >
                        <i className="ri-download-line align-bottom me-1"></i>
                        Download
                      </Button>
                    }
                    {/* Send Email for Split Redelivery (separate flow — re-delivery qty/date) */}
                    <Button
                      color="outline-primary"
                      size="sm"
                      onClick={() => {
                        setRowEmailItemId(item.id);
                        setRowEmailSplitId(split.id);
                        setRowEmailDeliveryDate(
                          normalizeDeliveryDateValue(split.re_delivery_date)
                        );
                        setRowEmailIsDefect(true);
                        setShowRowDeliveryEmailModal(true);
                      }}
                    >
                      <i className="ri-mail-send-line align-bottom me-1"></i>
                      Send Email
                    </Button>
                    {IsValidPermissions(["ADD_PURCHASE_DELIVERY"]) &&
                      <Button
                        color="success"
                        size="sm"
                        onClick={() => {
                          setCurrentSplit(split);
                          setUpdateRedeliveryData({
                            redelivered_quantity: split.re_delivery_quantity || 0,
                            redelivered_date: new Date()
                              .toISOString()
                              .split("T")[0],
                            redelivery_notes: split.re_delivery_notes || "",
                            attachments: [],
                            is_re_delivered: false,
                          });
                          setShowUpdateRedeliveryModal(true);
                        }}
                        disabled={isUpdatingRedelivery}
                      >
                        <i className="ri-check-double-line align-bottom me-1"></i>
                        Recivied
                      </Button>
                    }
                    {IsValidPermissions(["ADD_PURCHASE_DELIVERY"]) &&
                      <Button
                        color="warning"
                        size="sm"
                        onClick={() => {
                          setCurrentSplit(split);
                          setRedeliveryDefectData({
                            delivered_quantity: split.re_delivery_quantity || 0,
                            accepted_quantity: 0,
                            no_items_defected: split.re_delivery_quantity || 0,
                            delivery_notes: "",
                            delivered_date: new Date()
                              .toISOString()
                              .split("T")[0],
                            attachments: [],
                            is_defect_items: true,
                            is_re_delivered: false,
                          });
                          setShowRedeliveryDefectModal(true);
                        }}
                        disabled={isUpdatingRedelivery}
                      >
                        <i className="ri-error-warning-line align-bottom me-1"></i>
                        Defect
                      </Button>
                    }
                  </div>
                </td>
              </tr>
            )}
        </React.Fragment>
      );
    });
  };

  return (
    <>
      {/* Render split deliveries if item is split */}
      {item.is_split ? (
        renderSplitDeliveries()
      ) : (
        /* Normal item rendering */
        <>
          {/* Main Delivery Row */}
          <tr
            className={`${hasDefects ? "border-danger border-2" : ""} ${hasArrangedRedelivery ? "border-info border-2" : ""
              } ${isFullyDelivered ? "border-success border-2" : ""}`}
          >
            <td className="text-center">
              <div className="d-flex flex-column align-items-center">
                {!isFullyDelivered && (
                  <Input
                    type="checkbox"
                    className="form-check-input mb-1"
                    checked={
                      (item.delivery_type || "in_preparation") === "planned"
                    }
                    disabled={item.item_undeliverable}
                    onChange={(e) =>
                      handleItemPlanningCheckbox(item.id, e.target.checked)
                    }
                  />
                )}
                {isFullyDelivered && (
                  <span className="badge bg-soft-success text-success mb-1">
                    Delivered
                  </span>
                )}
                {(item.delivery_type || "in_preparation") ===
                "in_preparation" ? (
                  <>
                    <span className="text-warning fw-semibold fs-12">
                      In preparation
                    </span>
                    <span className="text-muted fs-11 text-center">
                      This delivery is in preparation and not planned yet.
                    </span>
                  </>
                ) : (
                  <span className="badge bg-soft-success text-success">
                    Planned
                  </span>
                )}
              </div>
            </td>
            <td>
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0 avatar-md bg-light rounded p-1">
                  <ProductThumbnail
              attachment_urls={item?.attachment_urls}
              alt={item?.product_name || ''}
              size="lg"
              className="me-2"
            />
                  {/* {item.thumbnail_url ? (
                    <img
                      src={item.thumbnail_url}
                      alt={item.product_name}
                      className="img-fluid d-block rounded"
                      style={{
                        width: "60px",
                        height: "60px",
                        objectFit: "cover",
                      }}
                    />
                  ) : null}
                  {!item.thumbnail_url && (
                    <div className="avatar-title bg-soft-primary text-primary rounded w-100 h-100 d-flex align-items-center justify-content-center">
                      <i className="ri-box-3-line fs-18"></i>
                    </div>
                  )} */}
                </div>
                <div className="flex-grow-1 ms-3">
                  <h6 className="fs-14 mb-1">
                    <span className="text-dark">{item.product_name}</span>
                  </h6>
                  <div className="text-muted fs-12">
                    Product ID: {item.product} • Item ID: {item.id}
                  </div>
                  {renderUserInfo(item)}
                  {getAllAttachments(item).length > 0 && (
                    <div className="mt-2">
                      <Button
                        color="outline-primary"
                        size="sm"
                        onClick={() =>
                          handleShowAttachments(
                            getAllAttachments(item),
                            item.product_name
                          )
                        }
                      >
                        <i className="ri-attachment-line align-bottom me-1"></i>
                        View Attachments ({getAllAttachments(item).length})
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </td>
            <td className="text-center">
              <span className="fw-bold">{getPoSupplierQuantity(item)}</span>
              {item.measurement_name && (
                <div className="text-muted fs-12">{item.measurement_name}</div>
              )}
            </td>
            <td className="text-center">
              {item.delivered_quantity !== undefined ? (
                <div>
                  <span className="fw-bold text-success">
                    {item.delivered_quantity}
                  </span>
                  {item.measurement_name && (
                    <div className="text-muted fs-12">
                      {item.measurement_name}
                    </div>
                  )}
                </div>
              ) : (
                <span className="text-muted">-</span>
              )}
            </td>
            <td className="text-center">
              {item.accepted_quantity !== undefined ? (
                <div>
                  <span className="fw-bold text-success">
                    {item.accepted_quantity}
                  </span>
                  {item.measurement_name && (
                    <div className="text-muted fs-12">
                      {item.measurement_name}
                    </div>
                  )}
                </div>
              ) : (
                <span className="text-muted">-</span>
              )}
            </td>
            <td className="text-center">
              {item.no_items_defected !== undefined ? (
                <div>
                  <span
                    className={`fw-bold ${item.no_items_defected > 0
                        ? "text-danger"
                        : "text-success"
                      }`}
                  >
                    {item.no_items_defected}
                  </span>
                  {item.measurement_name && (
                    <div className="text-muted fs-12">
                      {item.measurement_name}
                    </div>
                  )}
                </div>
              ) : (
                <span className="text-muted">-</span>
              )}
            </td>
            <td className="text-center">
              <div className="d-flex align-items-center justify-content-center">
                {item.po_expected_delivery_date ? (
                  <span className="badge bg-soft-info text-info me-2">
                    {new Date(
                      item.po_expected_delivery_date
                    ).toLocaleDateString()}
                  </span>
                ) : (
                  <span className="text-muted me-2">-</span>
                )}
                {IsValidPermissions(["ADD_PURCHASE_DELIVERY"]) &&
                  <Button
                    color="outline-primary"
                    size="sm"
                    className="p-1"
                    onClick={() => {
                      setEditDateData({
                        po_expected_delivery_date:
                          item.po_expected_delivery_date || "",
                      });
                      setShowEditDateModal(true);
                    }}
                    disabled={isUpdating || hasDeliveryRecords}
                  >
                    <i className="ri-edit-line fs-12"></i>
                  </Button>
                }
              </div>
            </td>
            <td className="text-center">
              {getDeliveryStatusBadge(getUpdatedDeliveryStatus(item))}
            </td>
            <td className="text-center">
              {item.delivered_date ? (
                <span className="badge bg-soft-success text-success">
                  {new Date(item.delivered_date).toLocaleDateString()}
                </span>
              ) : (
                <span className="text-muted">-</span>
              )}
            </td>
            <td className="text-center">
              <div className="d-flex align-items-center justify-content-center">
                {item.contact_person_phone ? (
                  <span className="badge bg-soft-info text-info me-2">
                    {item.contact_person_phone}
                  </span>
                ) : (
                  <span className="text-muted me-2">-</span>
                )}
              </div>
              {IsValidPermissions(["ADD_PURCHASE_DELIVERY"]) && (
                <Button
                  color="outline-secondary"
                  size="sm"
                  onClick={() => setShowContactModal(true)}
                  disabled={getUpdatedDeliveryStatus(item) === "delivered"}
                >
                  <i className="ri-phone-line align-bottom me-1"></i>
                </Button>
              )}
            </td>
            <td className="text-center">
              <div className="d-flex justify-content-center gap-2">
                {/* Download Button for Normal Delivery */}
                {IsValidPermissions(["DOWNLOAD_PURCHASE_DELIVERY"]) &&
                  <Button
                    color="outline-success"
                    size="sm"
                    onClick={() => handleDownloadDelivery(false)}
                    className="w-100"
                  >
                    <i className="ri-download-line align-bottom me-1"></i>
                    Download
                  </Button>
                }
                {(item.delivery_type || "in_preparation") ===
                  "in_preparation" && !isDeliveryCancelled && (
                  <Button
                    color="outline-primary"
                    size="sm"
                    onClick={() => {
                      setRowEmailItemId(item.id);
                      setRowEmailSplitId(null);
                      setRowEmailDeliveryDate(
                        normalizeDeliveryDateValue(
                          item.po_expected_delivery_date
                        )
                      );
                      setRowEmailIsDefect(false);
                      setShowRowDeliveryEmailModal(true);
                    }}
                    className="w-100"
                  >
                    <i className="ri-mail-send-line align-bottom me-1"></i>
                    Send Email
                  </Button>
                )}

                {/* Cancelled DR: send the cancellation email manually (attaches the cancelled PDF) */}
                {isDeliveryCancelled && (
                  <Button
                    color="outline-danger"
                    size="sm"
                    onClick={() => {
                      setRowEmailItemId(item.id);
                      setRowEmailSplitId(null);
                      setRowEmailDeliveryDate(
                        normalizeDeliveryDateValue(
                          item.po_expected_delivery_date
                        )
                      );
                      setShowRowDeliveryEmailModal(true);
                    }}
                    className="w-100"
                  >
                    <i className="ri-mail-send-line align-bottom me-1"></i>
                    Send Cancellation Email
                  </Button>
                )}

                {canSplitDelivery && !item.item_undeliverable && (
                  <>
                    {IsValidPermissions(["ADD_PURCHASE_DELIVERY"]) &&
                      <Button
                        color="outline-info"
                        size="sm"
                        onClick={() => {
                          setSplitDeliveryData({
                            splits: [
                              {
                                quantity: poOrderedQuantity,
                                delivery_date: new Date()
                                  .toISOString()
                                  .split("T")[0],
                              },
                            ],
                          });
                          setShowSplitDeliveryModal(true);
                        }}
                        disabled={isUpdating || hasDeliveryRecords || item.item_undeliverable}
                      >
                        <i className="ri-dashboard-horizontal-line align-bottom me-1"></i>
                        Split
                      </Button>
                    }
                  </>
                )}

                {(!item.delivery_status ||
                  item.delivery_status === "pending" ||
                  item.delivery_status === "partially_delivered") &&
                  !item.item_undeliverable && (
                    <>
                      {IsValidPermissions(["ADD_PURCHASE_DELIVERY"]) &&
                        <Button
                          color="success"
                          size="sm"
                          onClick={() => setShowDeliveryModal(true)}
                          disabled={isUpdating || item.item_undeliverable}
                        >
                          {isUpdating ? (
                            <Spinner size="sm" className="me-1" />
                          ) : (
                            <i className="ri-check-line align-bottom me-1"></i>
                          )}
                          Recivied
                        </Button>}
                      {IsValidPermissions(["ADD_PURCHASE_DELIVERY"]) &&
                        <Button
                          color="warning"
                          size="sm"
                          onClick={() => setShowDefectModal(true)}
                          disabled={isUpdating || item.item_undeliverable}
                        >
                          {isUpdating ? (
                            <Spinner size="sm" className="me-1" />
                          ) : (
                            <i className="ri-error-warning-line align-bottom me-1"></i>
                          )}
                          Defect
                        </Button>}
                      {/* Mark Undeliverable (SERVICE items only) */}
                      {canMarkUndeliverable && IsValidPermissions(["ADD_PURCHASE_DELIVERY"]) && (
                        <Button
                          color="danger"
                          size="sm"
                          onClick={() => {
                            setUndeliverableNotes(item.undeliverable_notes || "");
                            setUsedAmount("");
                            setShowUndeliverableModal(true);
                          }}
                          disabled={isUpdating}
                        >
                          <i className="ri-close-circle-line align-bottom me-1"></i>
                          Mark Undeliverable
                        </Button>
                      )}
                    </>
                  )}

                {/* Show undeliverable badge if marked */}
                {item.item_undeliverable && (
                  <Badge color="danger" className="fs-12">
                    <i className="ri-close-circle-line align-middle me-1"></i>
                    Undeliverable
                  </Badge>
                )}

                {/* Cancelled delivery request badge */}
                {isDeliveryCancelled && (
                  <Badge color="danger" className="fs-12">
                    <i className="ri-close-circle-line align-middle me-1"></i>
                    Delivery Request Cancelled
                  </Badge>
                )}

                {/* Always show update button (hidden when the delivery request is cancelled) */}
                {IsValidPermissions(["EDIT_PURCHASE_DELIVERY"]) && !isDeliveryCancelled &&
                  <Button
                    color="outline-primary"
                    size="sm"
                    onClick={() => {
                      setCurrentSplit(null); // Ensure currentSplit is null for normal items
                      setStatusUpdateData({
                        status: getUpdatedDeliveryStatus(item),
                        delivery_status: getUpdatedDeliveryStatus(item),
                        delivery_notes: item.delivery_notes || "",
                        delivered_quantity:
                          parseFloat(item.delivered_quantity) || 0,
                        accepted_quantity:
                          parseFloat(item.accepted_quantity) || 0,
                        no_items_defected:
                          parseFloat(item.no_items_defected) || 0,
                        delivered_date:
                          item.delivered_date ||
                          new Date().toISOString().split("T")[0],
                        is_defect_items: item.is_defect_items || false,
                        is_re_delivered: item.is_re_delivered || false,
                        contact_phone: item.contact_person_phone || "",
                        delivery_type: item.delivery_type || "in_preparation",
                      });
                      setDeliveryData((prev) => ({ ...prev, attachments: [] })); // Clear previous attachments
                      setShowUpdateStatusModal(true);
                    }}
                    disabled={isUpdating || item.item_undeliverable}
                  >
                    <i className="ri-edit-line align-bottom me-1"></i>
                    Update
                  </Button>}

                {/* Cancel Delivery Request (non-split item-level delivery) */}
                {!["cancelled", "delivered"].includes(
                  getUpdatedDeliveryStatus(item)
                ) &&
                  IsValidPermissions(["DELETE_PURCHASE_DELIVERY"]) && (
                    <Button
                      color="danger"
                      size="sm"
                      onClick={() => {
                        setCancelDRError(null);
                        setCancelDRTarget({ item_id: item.id });
                      }}
                    >
                      <i className="ri-close-circle-line align-bottom me-1"></i>
                      Cancel Delivery Request
                    </Button>
                  )}

                {/* FIXED: Show Arrange Redelivery button in main actions when applicable */}
                {canArrangeRedelivery && !item.item_undeliverable && (
                  <>
                    {IsValidPermissions(["ADD_PURCHASE_DELIVERY"]) &&
                      <Button
                        color="info"
                        size="sm"
                        onClick={() => {
                          setRedeliveryData({
                            redelivery_quantity: item.no_items_defected || 0,
                            redelivery_notes: "",
                            expected_delivery_date:
                              item.expected_delivery_date ||
                              new Date().toISOString().split("T")[0],
                          });
                          setShowRedeliveryModal(true);
                        }}
                        disabled={isUpdating || item.item_undeliverable}
                      >
                        <i className="ri-refresh-line align-bottom me-1"></i>
                        Arrange Redelivery
                      </Button>
                    }
                  </>
                )}
              </div>
            </td>
          </tr>

          {/* Undeliverable Information Row */}
          {item.item_undeliverable && (
            <tr className="bg-soft-danger border-danger border-start border-end border-bottom">
              <td colSpan={3} className="p-3 border-0">
                <div className="d-flex align-items-center">
                  <Badge color="danger" className="me-2">
                    <i className="ri-close-circle-line align-middle me-1"></i>
                    Item Marked as Undeliverable
                  </Badge>
                  {item.balance_quantity > 0 && (
                    <div className="text-muted fs-13 ms-2">
                      <strong>Balance Quantity:</strong> {item.balance_quantity} {item.measurement_name}
                    </div>
                  )}
                </div>
              </td>
              <td colSpan={4} className="p-3 border-0">
                {item.undeliverable_notes && (
                  <div className="text-muted fs-13">
                    <strong>Reason:</strong> {item.undeliverable_notes}
                  </div>
                )}
                {item.balance_cost > 0 && (
                  <div className="text-muted fs-13 mt-1">
                    <strong>Balance Cost:</strong> {item.balance_cost?.toFixed(2) || "0.00"}
                  </div>
                )}
              </td>
              <td colSpan={3} className="p-3 border-0 text-end">
                <Badge color="danger" className="fs-12">
                  Delivery Actions Disabled
                </Badge>
              </td>
            </tr>
          )}

          {/* Defect Information Row */}
          {(hasPendingDefects || hasRedeliveryDefects) &&
            getUpdatedDeliveryStatus(item) === "partially_delivered" &&
            !item.item_undeliverable && (
              <tr className="bg-soft-danger border-danger border-start border-end border-bottom">
                <td colSpan={3} className="p-3 border-0">
                  <div className="d-flex align-items-center">
                    <Badge color="danger" className="me-2">
                      <i className="ri-error-warning-line align-middle me-1"></i>
                      {hasRedeliveryDefects
                        ? "Redelivery Defect Details"
                        : "Defect Details"}
                    </Badge>
                    <div className="text-muted fs-13">
                      <strong>Defected Quantity:</strong>{" "}
                      {item.no_items_defected} {item.measurement_name}
                    </div>
                  </div>
                </td>
                <td colSpan={4} className="p-3 border-0">
                  {item.defect_description && (
                    <div className="text-muted fs-13">
                      <strong>Defect Notes:</strong> {item.defect_description}
                    </div>
                  )}
                  {item.defect_reported_date && (
                    <div className="text-muted fs-13">
                      <strong>Reported Date:</strong>{" "}
                      {new Date(item.defect_reported_date).toLocaleDateString()}
                    </div>
                  )}
                  {hasRedeliveryDefects && (
                    <div className="text-muted fs-13">
                      <strong>Cycle:</strong> Redelivery with defects - arrange
                      new redelivery
                    </div>
                  )}
                </td>
                <td colSpan={3} className="p-3 border-0 text-end">
                  {/* Download Button for Redelivery Defect */}
                  {/* {IsValidPermissions(["DOWNLOAD_PURCHASE_DELIVERY"]) &&
                    <Button
                      color="outline-success"
                      size="sm"
                      onClick={() => handleDownloadRedelivery(true)}
                      className="me-2"
                    >
                      <i className="ri-download-line align-bottom me-1"></i>
                      Download Redelivery
                    </Button>} */}

                  {/* FIXED: Show Arrange Redelivery button for both pending and redelivery defects */}
                  {(hasPendingDefects || hasRedeliveryDefects) && (
                    <>
                      {IsValidPermissions(["ADD_PURCHASE_DELIVERY"]) &&
                        <Button
                          color="info"
                          size="sm"
                          onClick={() => {
                            setRedeliveryData({
                              redelivery_quantity: item.no_items_defected || 0,
                              redelivery_notes: "",
                              expected_delivery_date:
                                item.expected_delivery_date ||
                                new Date().toISOString().split("T")[0],
                            });
                            setShowRedeliveryModal(true);
                          }}
                          disabled={isUpdating}
                        >
                          {isUpdating ? (
                            <Spinner size="sm" className="me-1" />
                          ) : (
                            <i className="ri-refresh-line align-bottom me-1"></i>
                          )}
                          {hasRedeliveryDefects
                            ? "Arrange New Redelivery"
                            : "Arrange Redelivery"}
                        </Button>
                      }
                    </>
                  )}
                </td>
              </tr>
            )}

          {/* Redelivery Information Row */}
          {hasArrangedRedelivery &&
            !item.is_re_delivered &&
            getUpdatedDeliveryStatus(item) === "partially_delivered" && (
              <tr className="bg-soft-info border-info border-start border-end border-bottom">
                <td colSpan={3} className="p-3 border-0">
                  <div className="d-flex align-items-center">
                    <Badge color="info" className="me-2">
                      <i className="ri-refresh-line align-middle me-1"></i>
                      Redelivery Arranged
                    </Badge>
                    <div className="text-muted fs-13">
                      <strong>Redelivery Quantity:</strong>{" "}
                      {item.re_delivery_quantity} {item.measurement_name}
                    </div>
                  </div>
                </td>
                <td colSpan={4} className="p-3 border-0">
                  {item.re_delivery_notes && (
                    <div className="text-muted fs-13">
                      <strong>Redelivery Notes:</strong>{" "}
                      {item.re_delivery_notes}
                    </div>
                  )}
                  {item.re_delivery_date && (
                    <div className="text-muted fs-13">
                      <strong>Scheduled Date:</strong>{" "}
                      {new Date(item.re_delivery_date).toLocaleDateString()}
                    </div>
                  )}
                </td>
                <td colSpan={3} className="p-3 border-0 text-end">
                  <div className="d-flex gap-2 justify-content-end">
                    {/* Download Button for Redelivery Arranged */}
                    {IsValidPermissions(["DOWNLOAD_PURCHASE_DELIVERY"]) &&
                      <Button
                        color="outline-success"
                        size="sm"
                        onClick={() => handleDownloadRedelivery(true)}
                      >
                        <i className="ri-download-line align-bottom me-1"></i>
                        Download
                      </Button>}
                    {/* Send Email for Redelivery (separate flow — re-delivery qty/date) */}
                    <Button
                      color="outline-primary"
                      size="sm"
                      onClick={() => {
                        setRowEmailItemId(item.id);
                        setRowEmailSplitId(null);
                        setRowEmailDeliveryDate(
                          normalizeDeliveryDateValue(item.re_delivery_date)
                        );
                        setRowEmailIsDefect(true);
                        setShowRowDeliveryEmailModal(true);
                      }}
                    >
                      <i className="ri-mail-send-line align-bottom me-1"></i>
                      Send Email
                    </Button>
                    {IsValidPermissions(["ADD_PURCHASE_DELIVERY"]) &&
                      <Button
                        color="success"
                        size="sm"
                        onClick={() => {
                          setUpdateRedeliveryData({
                            redelivered_quantity: item.re_delivery_quantity || 0,
                            redelivered_date: new Date()
                              .toISOString()
                              .split("T")[0],
                            redelivery_notes: item.re_delivery_notes || "",
                            attachments: [],
                            is_re_delivered: false,
                          });
                          setShowUpdateRedeliveryModal(true);
                        }}
                        disabled={isUpdatingRedelivery}
                      >
                        <i className="ri-check-double-line align-bottom me-1"></i>
                        Recivied
                      </Button>}
                    {IsValidPermissions(["ADD_PURCHASE_DELIVERY"]) &&
                      <Button
                        color="warning"
                        size="sm"
                        onClick={() => {
                          setRedeliveryDefectData({
                            delivered_quantity: item.re_delivery_quantity || 0,
                            accepted_quantity: 0,
                            no_items_defected: item.re_delivery_quantity || 0,
                            delivery_notes: "",
                            delivered_date: new Date()
                              .toISOString()
                              .split("T")[0],
                            attachments: [],
                            is_defect_items: true,
                            is_re_delivered: false,
                          });
                          setShowRedeliveryDefectModal(true);
                        }}
                        disabled={isUpdatingRedelivery}
                      >
                        <i className="ri-error-warning-line align-bottom me-1"></i>
                        Defect
                      </Button>}
                  </div>
                </td>
              </tr>
            )}
        </>
      )}

      {/* Contact Details Modal */}
      <ContactDetailsModal
        isOpen={showContactModal}
        toggle={() => {
          setShowContactModal(false);
          setCurrentSplit(null);
        }}
        item={currentSplit || item}
        onUpdateContact={handleUpdateContactPhone}
        isSplit={!!currentSplit}
        isLoading={isUpdating || isUpdatingSplit}
      />

      {/* Attachment Modal */}
      <AttachmentModal
        isOpen={showAttachmentsModal}
        toggle={() => setShowAttachmentsModal(false)}
        attachments={currentAttachments}
        title={currentAttachmentTitle}
      />
      <AuditLogModal
        isOpen={showAuditLogModal}
        toggle={() => setShowAuditLogModal(false)}
        auditLogs={currentAuditLogs}
        title={currentAuditTitle}
      />
      <CancelReasonModal
        show={cancelDRTarget !== null}
        title="Cancel Delivery Request"
        description="This will cancel the delivery request and email the supplier a cancellation notice. If items were partially received, the undelivered balance must first be marked undeliverable and moved to a new purchase order."
        isSubmitting={isCancellingDR}
        errorText={cancelDRError}
        onConfirm={handleCancelDeliveryRequest}
        onClose={() => setCancelDRTarget(null)}
      />
      <DownloadModal
        isOpen={isOpen}
        toggle={closeDownloadModal}
        status={status}
        permitNo={permitNo}
        errorMessage={errorMessage}
        message={message}
      />
      <DeliveryEmailModal
        isOpen={showRowDeliveryEmailModal}
        toggle={() => {
          setShowRowDeliveryEmailModal(false);
          setRowEmailDeliveryDate(null);
          setRowEmailItemId(null);
          setRowEmailSplitId(null);
          setRowEmailIsDefect(false);
        }}
        poId={item.purchase_order}
        poNumber={purchaseOrderNumber}
        deliveryDate={rowEmailDeliveryDate}
        itemId={rowEmailItemId}
        splitId={rowEmailSplitId}
        isDefectItems={rowEmailIsDefect}
        isRedelivery={rowEmailIsDefect}
      />

      {/* Mark Undeliverable Modal */}
      <Modal
        isOpen={showUndeliverableModal}
        toggle={() => {
          if (!isMarkingUndeliverable) {
            setShowUndeliverableModal(false);
            setUndeliverableNotes("");
            setUsedAmount("");
          }
        }}
      >
        <ModalHeader
          toggle={() => {
            if (!isMarkingUndeliverable) {
              setShowUndeliverableModal(false);
              setUndeliverableNotes("");
              setUsedAmount("");
            }
          }}
        >
          <i className="ri-close-circle-line me-2"></i>
          Mark Item as Undeliverable
        </ModalHeader>
        <ModalBody>
          <Form>
            <Alert color="warning" className="mb-3">
              <i className="ri-alert-line me-2"></i>
              This action will mark the item as undeliverable and disable delivery actions.
              The balance amount (original - used) will be available for splitting into new POs.
            </Alert>
            <FormGroup>
              <Label for="usedAmount">
                Amount Already Used <span className="text-muted">(from total_item_cost)</span>
              </Label>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <Input
                  type="number"
                  id="usedAmount"
                  min={0}
                  step={0.01}
                  value={usedAmount}
                  onChange={(e) => setUsedAmount(e.target.value)}
                  placeholder="0.00"
                  disabled={isMarkingUndeliverable}
                  style={{ maxWidth: 140 }}
                />
                <span className="text-muted">
                  Original: {Number(item.total_item_cost || 0).toFixed(2)} → Balance:{" "}
                  {Math.max(0, Number(item.total_item_cost || 0) - parseFloat(usedAmount || "0")).toFixed(2)}
                </span>
              </div>
            </FormGroup>
            <FormGroup>
              <Label for="undeliverableNotes">
                Reason for Undeliverable <span className="text-danger">*</span>
              </Label>
              <Input
                type="textarea"
                id="undeliverableNotes"
                rows={4}
                value={undeliverableNotes}
                onChange={(e) => setUndeliverableNotes(e.target.value)}
                placeholder="Please provide a reason why this item cannot be delivered..."
                disabled={isMarkingUndeliverable}
              />
            </FormGroup>
            {item.item_undeliverable && item.undeliverable_notes && (
              <Alert color="info" className="mt-2">
                <strong>Current Notes:</strong> {item.undeliverable_notes}
              </Alert>
            )}
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => {
              setShowUndeliverableModal(false);
              setUndeliverableNotes("");
              setUsedAmount("");
            }}
            disabled={isMarkingUndeliverable}
          >
            Cancel
          </Button>
          <Button
            color="danger"
            onClick={handleMarkUndeliverable}
            disabled={
              isMarkingUndeliverable ||
              !undeliverableNotes.trim() ||
              parseFloat(usedAmount || "0") > (parseFloat(item.total_item_cost) || 0)
            }
          >
            {isMarkingUndeliverable ? (
              <>
                <Spinner size="sm" className="me-2" />
                Marking...
              </>
            ) : (
              <>
                <i className="ri-close-circle-line align-bottom me-1"></i>
                Mark as Undeliverable
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delivery Modal */}
      <Modal
        isOpen={showDeliveryModal}
        toggle={() => {
          if (!isUpdating && !isUpdatingSplit) {
            setShowDeliveryModal(!showDeliveryModal);
            setCurrentSplit(null);
          }
        }}
        size="lg"
      >
        <ModalHeader
          toggle={() => {
            if (!isUpdating && !isUpdatingSplit) {
              setShowDeliveryModal(false);
              setCurrentSplit(null);
            }
          }}
        >
          {currentSplit
            ? `Mark Item as Received - Split #${currentSplit.batch_number}`
            : "Mark Item as Received"}
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label for="deliveredQuantity">Received Quantity</Label>
              <Input
                type="number"
                id="deliveredQuantity"
                value={deliveryData.delivered_quantity}
                onChange={(e) => {
                  const deliveredQty = parseFloat(e.target.value) || 0;
                  const maxQty = currentSplit
                    ? parseFloat(currentSplit.delivery_quantity)
                    : poOrderedQuantity;
                  const actualQty =
                    deliveredQty > maxQty ? maxQty : deliveredQty;

                  setDeliveryData({
                    ...deliveryData,
                    delivered_quantity: actualQty,
                    accepted_quantity: actualQty,
                  });
                }}
                max={
                  currentSplit ? currentSplit.delivery_quantity : poOrderedQuantity
                }
                min={0}
                disabled={isUpdating || isUpdatingSplit}
              />
              <div className="text-muted fs-12 mt-1">
                Maximum received quantity:{" "}
                {currentSplit ? currentSplit.delivery_quantity : poOrderedQuantity}
              </div>
            </FormGroup>
            <FormGroup>
              <Label for="deliveryDate">Received Date</Label>
              <Input
                type="date"
                id="deliveryDate"
                value={deliveryData.delivered_date}
                onChange={(e) =>
                  setDeliveryData({
                    ...deliveryData,
                    delivered_date: e.target.value,
                  })
                }
                disabled={isUpdating || isUpdatingSplit}
              />
            </FormGroup>
            <FormGroup>
              <Label for="deliveryNotes">Remarks</Label>
              <Input
                type="textarea"
                id="deliveryNotes"
                value={deliveryData.delivery_notes}
                onChange={(e) =>
                  setDeliveryData({
                    ...deliveryData,
                    delivery_notes: e.target.value,
                  })
                }
                placeholder="Add any delivery notes..."
                rows={3}
                disabled={isUpdating || isUpdatingSplit}
              />
            </FormGroup>
            <FormGroup>
              <Label for="deliveryAttachments">Delivery Proof (Required)</Label>
              <Input
                type="file"
                id="deliveryAttachments"
                multiple
                onChange={handleFileChange}
                accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                disabled={isUpdating || isUpdatingSplit}
              />
              <div className="text-muted fs-12 mt-1">
                Supported formats: JPG, PNG, PDF, DOC (Max 10MB per file)
              </div>
            </FormGroup>

            {deliveryData?.attachments.length > 0 && (
              <FormGroup>
                <Label className="fw-semibold">Selected Files:</Label>
                <div className="mt-2">
                  {deliveryData?.attachments?.map((file, index) => (
                    <div
                      key={index}
                      className="d-flex align-items-center justify-content-between bg-light rounded p-2 mb-2"
                    >
                      <div className="d-flex align-items-center">
                        <i className="ri-file-line text-muted me-2"></i>
                        <span className="fs-13">{file.name}</span>
                        <Badge color="light" className="ms-2 fs-11">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </Badge>
                      </div>
                      <Button
                        color="outline-danger"
                        size="sm"
                        onClick={() => removeFile(index)}
                        disabled={isUpdating || isUpdatingSplit}
                      >
                        <i className="ri-close-line"></i>
                      </Button>
                    </div>
                  ))}
                </div>
              </FormGroup>
            )}
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => {
              setShowDeliveryModal(false);
              setCurrentSplit(null);
            }}
            disabled={isUpdating || isUpdatingSplit}
          >
            Cancel
          </Button>
          <Button
            color="success"
            onClick={getDeliveryHandler()}
            disabled={
              isUpdating ||
              isUpdatingSplit ||
              deliveryData.attachments.length === 0
            }
          >
            {isUpdating || isUpdatingSplit ? (
              <>
                <Spinner size="sm" className="me-2" />
                Processing...
              </>
            ) : (
              "Mark as Delivered"
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Defect Modal */}
      <Modal
        isOpen={showDefectModal}
        toggle={() => {
          if (!isUpdating && !isUpdatingSplit) {
            setShowDefectModal(!showDefectModal);
            setCurrentSplit(null);
          }
        }}
        size="lg"
      >
        <ModalHeader
          toggle={() => {
            if (!isUpdating && !isUpdatingSplit) {
              setShowDefectModal(false);
              setCurrentSplit(null);
            }
          }}
        >
          {currentSplit
            ? `Raise Defect for Item - Split #${currentSplit.batch_number}`
            : "Raise Defect for Item"}
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label for="deliveredQuantityDefect">Received Quantity</Label>
              <Input
                type="number"
                id="deliveredQuantityDefect"
                value={deliveryData.delivered_quantity}
                onChange={(e) => {
                  const deliveredQty = parseFloat(e.target.value) || 0;
                  const maxQty = currentSplit
                    ? parseFloat(currentSplit.delivery_quantity)
                    : poOrderedQuantity;
                  const actualQty =
                    deliveredQty > maxQty ? maxQty : deliveredQty;

                  setDeliveryData({
                    ...deliveryData,
                    delivered_quantity: actualQty,
                  });
                }}
                max={
                  currentSplit ? currentSplit.delivery_quantity : poOrderedQuantity
                }
                min={0}
                disabled={isUpdating || isUpdatingSplit}
              />
              <div className="text-muted fs-12 mt-1">
                Maximum received quantity:{" "}
                {currentSplit ? currentSplit.delivery_quantity : poOrderedQuantity}
              </div>
            </FormGroup>
            <FormGroup>
              <Label for="acceptedQuantity">Accepted Quantity</Label>
              <Input
                type="number"
                id="acceptedQuantity"
                value={deliveryData.accepted_quantity}
                onChange={(e) => {
                  const acceptedQty = parseFloat(e.target.value) || 0;
                  const maxQty = deliveryData.delivered_quantity;
                  const actualQty = acceptedQty > maxQty ? maxQty : acceptedQty;

                  setDeliveryData({
                    ...deliveryData,
                    accepted_quantity: actualQty,
                    no_items_defected:
                      deliveryData.delivered_quantity - actualQty,
                  });
                }}
                max={deliveryData.delivered_quantity}
                min={0}
                disabled={isUpdating || isUpdatingSplit}
              />
              <div className="text-muted fs-12 mt-1">
                Maximum accepted quantity: {deliveryData.delivered_quantity}
              </div>
            </FormGroup>
            <FormGroup>
              <Label for="defectedQuantity">Defected Quantity</Label>
              <Input
                type="number"
                id="defectedQuantity"
                value={deliveryData.no_items_defected}
                onChange={(e) => {
                  const defectedQty = parseFloat(e.target.value) || 0;
                  const maxQty =
                    deliveryData.delivered_quantity -
                    deliveryData.accepted_quantity;
                  const actualQty = defectedQty > maxQty ? maxQty : defectedQty;

                  setDeliveryData({
                    ...deliveryData,
                    no_items_defected: actualQty,
                    accepted_quantity:
                      deliveryData.delivered_quantity - actualQty,
                  });
                }}
                max={
                  deliveryData.delivered_quantity -
                  deliveryData.accepted_quantity
                }
                min={0}
                disabled={isUpdating || isUpdatingSplit}
              />
              <div className="text-muted fs-12 mt-1">
                Maximum defected quantity:{" "}
                {deliveryData.delivered_quantity -
                  deliveryData.accepted_quantity}
              </div>
            </FormGroup>
            <FormGroup>
              <Label for="deliveryDateDefect">Received Date</Label>
              <Input
                type="date"
                id="deliveryDateDefect"
                value={deliveryData.delivered_date}
                onChange={(e) =>
                  setDeliveryData({
                    ...deliveryData,
                    delivered_date: e.target.value,
                  })
                }
                disabled={isUpdating || isUpdatingSplit}
              />
            </FormGroup>
            <FormGroup>
              <Label for="defectNotes">Defect Notes</Label>
              <Input
                type="textarea"
                id="defectNotes"
                value={deliveryData.delivery_notes}
                onChange={(e) =>
                  setDeliveryData({
                    ...deliveryData,
                    delivery_notes: e.target.value,
                  })
                }
                placeholder="Describe the defect issues..."
                rows={3}
                disabled={isUpdating || isUpdatingSplit}
              />
            </FormGroup>
            <FormGroup>
              <Label for="defectAttachments">Defect Evidence (Required)</Label>
              <Input
                type="file"
                id="defectAttachments"
                multiple
                onChange={handleFileChange}
                accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                disabled={isUpdating || isUpdatingSplit}
              />
              <div className="text-muted fs-12 mt-1">
                Upload photos or documents as evidence of defects
              </div>
            </FormGroup>

            {deliveryData?.attachments?.length > 0 && (
              <FormGroup>
                <Label className="fw-semibold">Selected Files:</Label>
                <div className="mt-2">
                  {deliveryData.attachments.map((file, index) => (
                    <div
                      key={index}
                      className="d-flex align-items-center justify-content-between bg-light rounded p-2 mb-2"
                    >
                      <div className="d-flex align-items-center">
                        <i className="ri-file-line text-muted me-2"></i>
                        <span className="fs-13">{file.name}</span>
                        <Badge color="light" className="ms-2 fs-11">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </Badge>
                      </div>
                      <Button
                        color="outline-danger"
                        size="sm"
                        onClick={() => removeFile(index)}
                        disabled={isUpdating || isUpdatingSplit}
                      >
                        <i className="ri-close-line"></i>
                      </Button>
                    </div>
                  ))}
                </div>
              </FormGroup>
            )}
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => {
              setShowDefectModal(false);
              setCurrentSplit(null);
            }}
            disabled={isUpdating || isUpdatingSplit}
          >
            Cancel
          </Button>
          <Button
            color="warning"
            onClick={getDefectHandler()}
            disabled={
              isUpdating ||
              isUpdatingSplit ||
              deliveryData.attachments.length === 0
            }
          >
            {isUpdating || isUpdatingSplit ? (
              <>
                <Spinner size="sm" className="me-2" />
                Processing...
              </>
            ) : (
              "Raise Defect"
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Stock Transfer Status Modal */}
      <Modal
        isOpen={showStockTransferModal}
        toggle={() => !isUpdatingStockTransfer && setShowStockTransferModal(false)}
        size="md"
      >
        <ModalHeader
          toggle={() =>
            !isUpdatingStockTransfer && setShowStockTransferModal(false)
          }
        >
          Update Stock Transfer Status
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label>Current Status</Label>
              <div>
                <Badge color="info">
                  {selectedStockAllocation?.transfer_status || "-"}
                </Badge>
              </div>
            </FormGroup>
            <FormGroup>
              <Label for="stockTransferStatus">Next Status</Label>
              <Input
                id="stockTransferStatus"
                type="select"
                value={stockTransferData.transfer_status}
                onChange={(e) =>
                  setStockTransferData((prev) => ({
                    ...prev,
                    transfer_status: e.target.value as
                      | "in_transit"
                      | "received"
                      | "partial_received"
                      | "cancelled",
                  }))
                }
                disabled={isUpdatingStockTransfer}
              >
                <option value="in_transit">In Transit</option>
                <option value="received">Received</option>
                <option value="partial_received">Partial Received</option>
                <option value="cancelled">Cancelled</option>
              </Input>
            </FormGroup>
            {(stockTransferData.transfer_status === "received" ||
              stockTransferData.transfer_status === "partial_received") && (
              <FormGroup>
                <Label for="stockReceivedQty">Received Quantity</Label>
                <Input
                  id="stockReceivedQty"
                  type="number"
                  min={0}
                  step="0.01"
                  value={stockTransferData.quantity}
                  onChange={(e) =>
                    setStockTransferData((prev) => ({
                      ...prev,
                      quantity: parseFloat(e.target.value || "0") || 0,
                    }))
                  }
                  disabled={isUpdatingStockTransfer}
                />
                <div className="text-muted fs-12 mt-1">
                  Max:{" "}
                  {selectedStockAllocation?.issued_qty ||
                    selectedStockAllocation?.allocated_from_stock_qty ||
                    0}
                </div>
              </FormGroup>
            )}

            {(stockTransferData.transfer_status === "received" ||
              stockTransferData.transfer_status === "partial_received") && (
              <>
                <hr className="my-2" />
                <div className="fw-semibold mb-2">
                  Delivery Details <span className="text-danger">*</span>
                </div>
                <FormGroup>
                  <Label for="stockTransferContactItem">
                    Delivery Person Contact
                  </Label>
                  <Input
                    id="stockTransferContactItem"
                    type="text"
                    value={stockTransferData.delivery_person_contact}
                    onChange={(e) =>
                      setStockTransferData((prev) => ({
                        ...prev,
                        delivery_person_contact: e.target.value,
                      }))
                    }
                    disabled={isUpdatingStockTransfer}
                    placeholder="Name / phone of the delivery person"
                  />
                </FormGroup>
                <FormGroup>
                  <Label for="stockTransferDateItem">Delivery Date</Label>
                  <Input
                    id="stockTransferDateItem"
                    type="date"
                    value={stockTransferData.delivery_date}
                    onChange={(e) =>
                      setStockTransferData((prev) => ({
                        ...prev,
                        delivery_date: e.target.value,
                      }))
                    }
                    disabled={isUpdatingStockTransfer}
                  />
                </FormGroup>
                <FormGroup>
                  <Label for="stockTransferArriveFromItem">
                    Items Arrive From
                  </Label>
                  <Input
                    id="stockTransferArriveFromItem"
                    type="text"
                    value={stockTransferData.items_arrive_from}
                    onChange={(e) =>
                      setStockTransferData((prev) => ({
                        ...prev,
                        items_arrive_from: e.target.value,
                      }))
                    }
                    disabled={isUpdatingStockTransfer}
                    placeholder="Where the items are arriving from"
                  />
                </FormGroup>
              </>
            )}

            {stockTransferError && (
              <div className="alert alert-danger py-2 mb-0">
                {stockTransferError}
              </div>
            )}
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setShowStockTransferModal(false)}
            disabled={isUpdatingStockTransfer}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleSubmitStockTransferUpdate}
            disabled={isUpdatingStockTransfer}
          >
            {isUpdatingStockTransfer ? (
              <>
                <Spinner size="sm" className="me-2" />
                Updating...
              </>
            ) : (
              "Update Status"
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Redelivery Modal */}
      <Modal
        isOpen={showRedeliveryModal}
        toggle={() => {
          if (!isUpdating && !isUpdatingSplit) {
            setShowRedeliveryModal(!showRedeliveryModal);
            setCurrentSplit(null);
          }
        }}
      >
        <ModalHeader
          toggle={() => {
            if (!isUpdating && !isUpdatingSplit) {
              setShowRedeliveryModal(false);
              setCurrentSplit(null);
            }
          }}
        >
          {currentSplit
            ? `Arrange Redelivery for Defected Items - Split #${currentSplit.batch_number}`
            : "Arrange Redelivery for Defected Items"}
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label for="redeliveryQuantity">Redelivery Quantity</Label>
              <Input
                type="number"
                id="redeliveryQuantity"
                value={redeliveryData.redelivery_quantity}
                onChange={(e) => {
                  const quantity = parseFloat(e.target.value) || 0;
                  const maxQuantity = currentSplit
                    ? currentSplit.no_items_defected
                    : item.no_items_defected;
                  const actualQuantity =
                    quantity > maxQuantity ? maxQuantity : quantity;

                  setRedeliveryData({
                    ...redeliveryData,
                    redelivery_quantity:
                      actualQuantity > 0 ? actualQuantity : 1, // Ensure at least 1
                  });
                }}
                max={
                  currentSplit
                    ? currentSplit.no_items_defected
                    : item.no_items_defected
                }
                min={1} // Minimum 1 to prevent zero/null
                disabled={isUpdating || isUpdatingSplit}
                required // Make it required
              />
              <div className="text-muted fs-12 mt-1">
                Maximum redelivery quantity:{" "}
                {currentSplit
                  ? currentSplit.no_items_defected
                  : item.no_items_defected}
              </div>
            </FormGroup>
            <FormGroup>
              <Label for="redeliveryExpectedDate">
                Expected Redelivery Date
              </Label>
              <Input
                type="date"
                id="redeliveryExpectedDate"
                value={redeliveryData.expected_delivery_date}
                onChange={(e) =>
                  setRedeliveryData({
                    ...redeliveryData,
                    expected_delivery_date: e.target.value,
                  })
                }
                disabled={isUpdating || isUpdatingSplit}
              />
            </FormGroup>
            <FormGroup>
              <Label for="redeliveryNotes">Redelivery Notes</Label>
              <Input
                type="textarea"
                id="redeliveryNotes"
                value={redeliveryData.redelivery_notes}
                onChange={(e) =>
                  setRedeliveryData({
                    ...redeliveryData,
                    redelivery_notes: e.target.value,
                  })
                }
                placeholder="Add any notes about the redelivery arrangement..."
                rows={3}
                disabled={isUpdating || isUpdatingSplit}
              />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => {
              setShowRedeliveryModal(false);
              setCurrentSplit(null);
            }}
            disabled={isUpdating || isUpdatingSplit}
          >
            Cancel
          </Button>
          <Button
            color="info"
            onClick={getRedeliveryHandler()}
            disabled={
              isUpdating ||
              isUpdatingSplit ||
              redeliveryData.redelivery_quantity <= 0 || // Ensure quantity is positive
              !redeliveryData.redelivery_quantity // Ensure quantity exists
            }
          >
            {isUpdating || isUpdatingSplit ? (
              <>
                <Spinner size="sm" className="me-2" />
                Processing...
              </>
            ) : (
              "Arrange Redelivery"
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Update Redelivery Status Modal */}
      <Modal
        isOpen={showUpdateRedeliveryModal}
        toggle={() => {
          if (!isUpdatingRedelivery) {
            setShowUpdateRedeliveryModal(!showUpdateRedeliveryModal);
            setCurrentSplit(null);
          }
        }}
        size="lg"
      >
        <ModalHeader
          toggle={() => {
            if (!isUpdatingRedelivery) {
              setShowUpdateRedeliveryModal(false);
              setCurrentSplit(null);
            }
          }}
        >
          {currentSplit
            ? `Update Redelivery Status - Split #${currentSplit.batch_number}`
            : "Update Redelivery Status"}
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label for="redeliveredQuantity">Redelivered Quantity</Label>
              <Input
                type="number"
                id="redeliveredQuantity"
                value={updateRedeliveryData.redelivered_quantity}
                onChange={(e) => {
                  const quantity = parseFloat(e.target.value) || 0;
                  const maxQuantity = currentSplit
                    ? currentSplit.re_delivery_quantity
                    : item.re_delivery_quantity;
                  setUpdateRedeliveryData({
                    ...updateRedeliveryData,
                    redelivered_quantity:
                      quantity > maxQuantity ? maxQuantity : quantity,
                  });
                }}
                max={
                  currentSplit
                    ? currentSplit.re_delivery_quantity
                    : item.re_delivery_quantity
                }
                min={1}
                disabled={isUpdatingRedelivery}
              />
              <div className="text-muted fs-12 mt-1">
                Maximum redelivered quantity:{" "}
                {currentSplit
                  ? currentSplit.re_delivery_quantity
                  : item.re_delivery_quantity}
              </div>
            </FormGroup>
            <FormGroup>
              <Label for="redeliveredDate">Redelivered Date</Label>
              <Input
                type="date"
                id="redeliveredDate"
                value={updateRedeliveryData.redelivered_date}
                onChange={(e) =>
                  setUpdateRedeliveryData({
                    ...updateRedeliveryData,
                    redelivered_date: e.target.value,
                  })
                }
                disabled={isUpdatingRedelivery}
              />
            </FormGroup>
            <FormGroup>
              <Label for="redeliveryStatusNotes">Redelivery Notes</Label>
              <Input
                type="textarea"
                id="redeliveryStatusNotes"
                value={updateRedeliveryData.redelivery_notes}
                onChange={(e) =>
                  setUpdateRedeliveryData({
                    ...updateRedeliveryData,
                    redelivery_notes: e.target.value,
                  })
                }
                placeholder="Add any notes about the redelivery..."
                rows={3}
                disabled={isUpdatingRedelivery}
              />
            </FormGroup>
            <FormGroup>
              <Label for="redeliveryAttachments">
                Redelivery Proof (Required)
              </Label>
              <Input
                type="file"
                id="redeliveryAttachments"
                multiple
                onChange={(e) => handleFileChange(e, "redelivery_attachments")}
                accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                disabled={isUpdatingRedelivery}
              />
              <div className="text-muted fs-12 mt-1">
                Upload photos or documents as proof of redelivery
              </div>
            </FormGroup>

            {updateRedeliveryData?.attachments.length > 0 && (
              <FormGroup>
                <Label className="fw-semibold">Selected Files:</Label>
                <div className="mt-2">
                  {updateRedeliveryData?.attachments?.map((file, index) => (
                    <div
                      key={index}
                      className="d-flex align-items-center justify-content-between bg-light rounded p-2 mb-2"
                    >
                      <div className="d-flex align-items-center">
                        <i className="ri-file-line text-muted me-2"></i>
                        <span className="fs-13">{file.name}</span>
                        <Badge color="light" className="ms-2 fs-11">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </Badge>
                      </div>
                      <Button
                        color="outline-danger"
                        size="sm"
                        onClick={() =>
                          removeFile(index, "redelivery_attachments")
                        }
                        disabled={isUpdatingRedelivery}
                      >
                        <i className="ri-close-line"></i>
                      </Button>
                    </div>
                  ))}
                </div>
              </FormGroup>
            )}
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => {
              setShowUpdateRedeliveryModal(false);
              setCurrentSplit(null);
            }}
            disabled={isUpdatingRedelivery}
          >
            Cancel
          </Button>
          <Button
            color="success"
            onClick={getUpdateRedeliveryHandler()}
            disabled={
              isUpdatingRedelivery ||
              updateRedeliveryData.attachments.length === 0
            }
          >
            {isUpdatingRedelivery ? (
              <>
                <Spinner size="sm" className="me-2" />
                Processing...
              </>
            ) : (
              "Mark as Redelivered"
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Redelivery Defect Modal */}
      <Modal
        isOpen={showRedeliveryDefectModal}
        toggle={() => {
          if (!isUpdatingRedelivery) {
            setShowRedeliveryDefectModal(!showRedeliveryDefectModal);
            setCurrentSplit(null);
          }
        }}
        size="lg"
      >
        <ModalHeader
          toggle={() => {
            if (!isUpdatingRedelivery) {
              setShowRedeliveryDefectModal(false);
              setCurrentSplit(null);
            }
          }}
        >
          {currentSplit
            ? `Raise Defect for Redelivery - Split #${currentSplit.batch_number}`
            : "Raise Defect for Redelivery"}
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label for="redeliveryDeliveredQuantity">
                Received Redelivery Quantity
              </Label>
              <Input
                type="number"
                id="redeliveryDeliveredQuantity"
                value={redeliveryDefectData.delivered_quantity}
                onChange={(e) => {
                  const deliveredQty = parseFloat(e.target.value) || 0;
                  const maxQty = currentSplit
                    ? currentSplit.re_delivery_quantity
                    : item.re_delivery_quantity;
                  const actualQty =
                    deliveredQty > maxQty ? maxQty : deliveredQty;

                  setRedeliveryDefectData({
                    ...redeliveryDefectData,
                    delivered_quantity: actualQty,
                    no_items_defected: actualQty,
                  });
                }}
                max={
                  currentSplit
                    ? currentSplit.re_delivery_quantity
                    : item.re_delivery_quantity
                }
                min={0}
                disabled={isUpdatingRedelivery}
              />
              <div className="text-muted fs-12 mt-1">
                Maximum received quantity:{" "}
                {currentSplit
                  ? currentSplit.re_delivery_quantity
                  : item.re_delivery_quantity}
              </div>
            </FormGroup>
            <FormGroup>
              <Label for="redeliveryAcceptedQuantity">
                Accepted Quantity from Redelivery
              </Label>
              <Input
                type="number"
                id="redeliveryAcceptedQuantity"
                value={redeliveryDefectData.accepted_quantity}
                onChange={(e) => {
                  const acceptedQty = parseFloat(e.target.value) || 0;
                  const maxQty = redeliveryDefectData.delivered_quantity;
                  const actualQty = acceptedQty > maxQty ? maxQty : acceptedQty;

                  setRedeliveryDefectData({
                    ...redeliveryDefectData,
                    accepted_quantity: actualQty,
                    no_items_defected:
                      redeliveryDefectData.delivered_quantity - actualQty,
                  });
                }}
                max={redeliveryDefectData.delivered_quantity}
                min={0}
                disabled={isUpdatingRedelivery}
              />
              <div className="text-muted fs-12 mt-1">
                Maximum accepted quantity:{" "}
                {redeliveryDefectData.delivered_quantity}
              </div>
            </FormGroup>
            <FormGroup>
              <Label for="redeliveryDefectedQuantity">
                Defected Quantity from Redelivery
              </Label>
              <Input
                type="number"
                id="redeliveryDefectedQuantity"
                value={redeliveryDefectData.no_items_defected}
                onChange={(e) => {
                  const defectedQty = parseFloat(e.target.value) || 0;
                  const maxQty =
                    redeliveryDefectData.delivered_quantity -
                    redeliveryDefectData.accepted_quantity;
                  const actualQty = defectedQty > maxQty ? maxQty : defectedQty;

                  setRedeliveryDefectData({
                    ...redeliveryDefectData,
                    no_items_defected: actualQty,
                    accepted_quantity:
                      redeliveryDefectData.delivered_quantity - actualQty,
                  });
                }}
                max={
                  redeliveryDefectData.delivered_quantity -
                  redeliveryDefectData.accepted_quantity
                }
                min={0}
                disabled={isUpdatingRedelivery}
              />
              <div className="text-muted fs-12 mt-1">
                Maximum defected quantity:{" "}
                {redeliveryDefectData.delivered_quantity -
                  redeliveryDefectData.accepted_quantity}
              </div>
            </FormGroup>
            <FormGroup>
              <Label for="redeliveryDefectDate">Received Date</Label>
              <Input
                type="date"
                id="redeliveryDefectDate"
                value={redeliveryDefectData.delivered_date}
                onChange={(e) =>
                  setRedeliveryDefectData({
                    ...redeliveryDefectData,
                    delivered_date: e.target.value,
                  })
                }
                disabled={isUpdatingRedelivery}
              />
            </FormGroup>
            <FormGroup>
              <Label for="redeliveryDefectNotes">Defect Notes</Label>
              <Input
                type="textarea"
                id="redeliveryDefectNotes"
                value={redeliveryDefectData.delivery_notes}
                onChange={(e) =>
                  setRedeliveryDefectData({
                    ...redeliveryDefectData,
                    delivery_notes: e.target.value,
                  })
                }
                placeholder="Describe the defect issues in redelivery..."
                rows={3}
                disabled={isUpdatingRedelivery}
              />
            </FormGroup>
            <FormGroup>
              <Label for="redeliveryDefectAttachments">
                Defect Evidence (Required)
              </Label>
              <Input
                type="file"
                id="redeliveryDefectAttachments"
                multiple
                onChange={(e) =>
                  handleFileChange(e, "redelivery_defect_attachments")
                }
                accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                disabled={isUpdatingRedelivery}
              />
              <div className="text-muted fs-12 mt-1">
                Upload photos or documents as evidence of defects in redelivery
              </div>
            </FormGroup>

            {redeliveryDefectData?.attachments?.length > 0 && (
              <FormGroup>
                <Label className="fw-semibold">Selected Files:</Label>
                <div className="mt-2">
                  {redeliveryDefectData.attachments.map((file, index) => (
                    <div
                      key={index}
                      className="d-flex align-items-center justify-content-between bg-light rounded p-2 mb-2"
                    >
                      <div className="d-flex align-items-center">
                        <i className="ri-file-line text-muted me-2"></i>
                        <span className="fs-13">{file.name}</span>
                        <Badge color="light" className="ms-2 fs-11">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </Badge>
                      </div>
                      <Button
                        color="outline-danger"
                        size="sm"
                        onClick={() =>
                          removeFile(index, "redelivery_defect_attachments")
                        }
                        disabled={isUpdatingRedelivery}
                      >
                        <i className="ri-close-line"></i>
                      </Button>
                    </div>
                  ))}
                </div>
              </FormGroup>
            )}
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => {
              setShowRedeliveryDefectModal(false);
              setCurrentSplit(null);
            }}
            disabled={isUpdatingRedelivery}
          >
            Cancel
          </Button>
          <Button
            color="warning"
            onClick={getRedeliveryDefectHandler()}
            disabled={
              isUpdatingRedelivery ||
              redeliveryDefectData.attachments.length === 0
            }
          >
            {isUpdatingRedelivery ? (
              <>
                <Spinner size="sm" className="me-2" />
                Processing...
              </>
            ) : (
              "Raise Defect"
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Edit Expected Date Modal */}
      <Modal
        isOpen={showEditDateModal}
        toggle={() => !isUpdating && setShowEditDateModal(!showEditDateModal)}
      >
        <ModalHeader toggle={() => !isUpdating && setShowEditDateModal(false)}>
          Edit Expected Delivery Date
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label for="expectedDeliveryDate">Expected Delivery Date</Label>
              <Input
                type="date"
                id="expectedDeliveryDate"
                value={editDateData.po_expected_delivery_date}
                onChange={(e) =>
                  setEditDateData({
                    ...editDateData,
                    po_expected_delivery_date: e.target.value,
                  })
                }
                disabled={isUpdating}
              />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setShowEditDateModal(false)}
            disabled={isUpdating}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleEditExpectedDate}
            disabled={isUpdating || !editDateData.po_expected_delivery_date}
          >
            {isUpdating ? (
              <>
                <Spinner size="sm" className="me-2" />
                Updating...
              </>
            ) : (
              "Update Date"
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Update Status Modal */}
      <Modal
        isOpen={showUpdateStatusModal}
        toggle={() =>
          !isUpdating &&
          !isUpdatingSplit &&
          setShowUpdateStatusModal(!showUpdateStatusModal)
        }
        size="lg"
      >
        <ModalHeader
          toggle={() =>
            !isUpdating && !isUpdatingSplit && setShowUpdateStatusModal(false)
          }
        >
          {currentSplit
            ? `Update Delivery Status - Split #${currentSplit.batch_number}`
            : "Update Delivery Status"}
          {currentSplit && (
            <Badge color="info" className="ms-2">
              Split Item
            </Badge>
          )}
        </ModalHeader>
        <ModalBody>
          <Form>
            {/* Split Information Display */}
            {currentSplit && (
              <Alert color="info" className="fs-13">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>Split Delivery #{currentSplit.batch_number}</strong>
                    <div className="text-muted">
                      Original Quantity: {currentSplit.delivery_quantity}{" "}
                      {item.measurement_name}
                    </div>
                  </div>
                  <Badge color="info">Split ID: {currentSplit.id}</Badge>
                </div>
              </Alert>
            )}

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="deliveryStatus">Delivery Status</Label>
                  <Input
                    type="select"
                    id="deliveryStatus"
                    value={statusUpdateData.delivery_status}
                    onChange={(e) =>
                      setStatusUpdateData({
                        ...statusUpdateData,
                        delivery_status: e.target.value,
                        status: e.target.value,
                      })
                    }
                    disabled={isUpdating || isUpdatingSplit}
                  >
                    <option value="pending">Pending</option>
                    <option value="in_progress">In Progress</option>
                    <option value="partially_delivered">
                      Partially Delivered
                    </option>
                    <option value="delivered">Delivered</option>
                    <option value="defected">Defected</option>
                    <option value="redelivery_arranged">
                      Redelivery Arranged
                    </option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="deliveredQuantityUpdate">
                    Delivered Quantity
                    <span className="text-muted fs-12 ms-1">
                      (Max:{" "}
                      {currentSplit
                        ? currentSplit.delivery_quantity
                        : poOrderedQuantity}
                      )
                    </span>
                  </Label>
                  <Input
                    type="number"
                    id="deliveredQuantityUpdate"
                    value={statusUpdateData.delivered_quantity}
                    onChange={(e) => {
                      const maxQty = currentSplit
                        ? currentSplit.delivery_quantity
                        : poOrderedQuantity;
                      const deliveredQty = parseFloat(e.target.value) || 0;
                      const actualQty =
                        deliveredQty > maxQty ? maxQty : deliveredQty;

                      setStatusUpdateData({
                        ...statusUpdateData,
                        delivered_quantity: actualQty,
                      });
                    }}
                    max={
                      currentSplit
                        ? currentSplit.delivery_quantity
                        : poOrderedQuantity
                    }
                    min={0}
                    step="0.01"
                    disabled={isUpdating || isUpdatingSplit}
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="acceptedQuantityUpdate">
                    Accepted Quantity
                    <span className="text-muted fs-12 ms-1">
                      (Max: {statusUpdateData.delivered_quantity})
                    </span>
                  </Label>
                  <Input
                    type="number"
                    id="acceptedQuantityUpdate"
                    value={statusUpdateData.accepted_quantity}
                    onChange={(e) => {
                      const maxQty = statusUpdateData.delivered_quantity;
                      const acceptedQty = parseFloat(e.target.value) || 0;
                      const actualQty =
                        acceptedQty > maxQty ? maxQty : acceptedQty;

                      setStatusUpdateData({
                        ...statusUpdateData,
                        accepted_quantity: actualQty,
                        no_items_defected:
                          statusUpdateData.delivered_quantity - actualQty,
                      });
                    }}
                    max={statusUpdateData.delivered_quantity}
                    min={0}
                    step="0.01"
                    disabled={isUpdating || isUpdatingSplit}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="defectedQuantityUpdate">
                    Defected Quantity
                    <span className="text-muted fs-12 ms-1">
                      (Max:{" "}
                      {statusUpdateData.delivered_quantity -
                        statusUpdateData.accepted_quantity}
                      )
                    </span>
                  </Label>
                  <Input
                    type="number"
                    id="defectedQuantityUpdate"
                    value={statusUpdateData.no_items_defected}
                    onChange={(e) => {
                      const maxQty =
                        statusUpdateData.delivered_quantity -
                        statusUpdateData.accepted_quantity;
                      const defectedQty = parseFloat(e.target.value) || 0;
                      const actualQty =
                        defectedQty > maxQty ? maxQty : defectedQty;

                      setStatusUpdateData({
                        ...statusUpdateData,
                        no_items_defected: actualQty,
                        accepted_quantity:
                          statusUpdateData.delivered_quantity - actualQty,
                      });
                    }}
                    max={
                      statusUpdateData.delivered_quantity -
                      statusUpdateData.accepted_quantity
                    }
                    min={0}
                    step="0.01"
                    disabled={isUpdating || isUpdatingSplit}
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="deliveryDateUpdate">Delivery Date</Label>
                  <Input
                    type="date"
                    id="deliveryDateUpdate"
                    value={statusUpdateData.delivered_date}
                    onChange={(e) =>
                      setStatusUpdateData({
                        ...statusUpdateData,
                        delivered_date: e.target.value,
                      })
                    }
                    disabled={isUpdating || isUpdatingSplit}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="contactPhoneUpdate">Contact Phone</Label>
                  <Input
                    type="text"
                    id="contactPhoneUpdate"
                    value={statusUpdateData.contact_phone}
                    onChange={(e) =>
                      setStatusUpdateData({
                        ...statusUpdateData,
                        contact_phone: e.target.value,
                      })
                    }
                    placeholder="Enter contact phone"
                    disabled={isUpdating || isUpdatingSplit}
                  />
                </FormGroup>
              </Col>
            </Row>

            {/* Flag checkboxes for manual control */}
            <Row>
              <Col md={6}>
                <FormGroup check>
                  <Input
                    type="checkbox"
                    id="isDefectItems"
                    checked={statusUpdateData.is_defect_items}
                    onChange={(e) =>
                      setStatusUpdateData({
                        ...statusUpdateData,
                        is_defect_items: e.target.checked,
                      })
                    }
                    disabled={isUpdating || isUpdatingSplit}
                  />
                  <Label for="isDefectItems" check>
                    Mark as Defect Items
                  </Label>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup check>
                  <Input
                    type="checkbox"
                    id="isReDelivered"
                    checked={statusUpdateData.is_re_delivered}
                    onChange={(e) =>
                      setStatusUpdateData({
                        ...statusUpdateData,
                        is_re_delivered: e.target.checked,
                      })
                    }
                    disabled={isUpdating || isUpdatingSplit}
                  />
                  <Label for="isReDelivered" check>
                    Mark as Re-delivered
                  </Label>
                </FormGroup>
              </Col>
            </Row>

            <FormGroup>
              <Label for="deliveryNotesUpdate">Delivery Notes</Label>
              <Input
                type="textarea"
                id="deliveryNotesUpdate"
                value={statusUpdateData.delivery_notes}
                onChange={(e) =>
                  setStatusUpdateData({
                    ...statusUpdateData,
                    delivery_notes: e.target.value,
                  })
                }
                placeholder="Add any delivery notes..."
                rows={3}
                disabled={isUpdating || isUpdatingSplit}
              />
            </FormGroup>

            {/* Attachment Section for Update */}
            <FormGroup>
              <Label for="updateAttachments">
                Update Attachments
                {deliveryData.attachments.length > 0 && (
                  <Badge color="success" className="ms-2">
                    {deliveryData.attachments.length} file(s) selected
                  </Badge>
                )}
              </Label>
              <Input
                type="file"
                id="updateAttachments"
                multiple
                onChange={handleFileChange}
                accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                disabled={isUpdating || isUpdatingSplit}
              />
              <div className="text-muted fs-12 mt-1">
                Add supporting documents for this update (delivery proof, defect
                evidence, etc.)
              </div>
            </FormGroup>

            {deliveryData.attachments.length > 0 && (
              <FormGroup>
                <Label className="fw-semibold">Selected Files:</Label>
                <div className="mt-2">
                  {deliveryData.attachments.map((file, index) => (
                    <div
                      key={index}
                      className="d-flex align-items-center justify-content-between bg-light rounded p-2 mb-2"
                    >
                      <div className="d-flex align-items-center">
                        <i className="ri-file-line text-muted me-2"></i>
                        <span className="fs-13">{file.name}</span>
                        <Badge color="light" className="ms-2 fs-11">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </Badge>
                      </div>
                      <Button
                        color="outline-danger"
                        size="sm"
                        onClick={() => removeFile(index)}
                        disabled={isUpdating || isUpdatingSplit}
                      >
                        <i className="ri-close-line"></i>
                      </Button>
                    </div>
                  ))}
                </div>
              </FormGroup>
            )}
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => {
              setShowUpdateStatusModal(false);
              setCurrentSplit(null);
              setDeliveryData((prev) => ({ ...prev, attachments: [] }));
            }}
            disabled={isUpdating || isUpdatingSplit}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleUpdateStatus}
            disabled={isUpdating || isUpdatingSplit}
          >
            {isUpdating || isUpdatingSplit ? (
              <>
                <Spinner size="sm" className="me-2" />
                {currentSplit ? "Updating Split..." : "Updating..."}
              </>
            ) : (
              <>
                <i className="ri-save-line align-bottom me-1"></i>
                Update Status
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Split Delivery Modal */}
      <Modal
        isOpen={showSplitDeliveryModal}
        toggle={() =>
          !isSplitting && setShowSplitDeliveryModal(!showSplitDeliveryModal)
        }
        size="lg"
      >
        <ModalHeader
          toggle={() => !isSplitting && setShowSplitDeliveryModal(false)}
        >
          Split Delivery for {item.product_name}
        </ModalHeader>
        <ModalBody>
          <Alert color="info" className="fs-13">
            <i className="ri-information-line me-2"></i>
            Split the delivery into multiple shipments. The total quantity must
            equal the ordered quantity:{" "}
            <strong>
              {poOrderedQuantity} {item.measurement_name}
            </strong>
          </Alert>

          <Form>
            {splitDeliveryData.splits.map((delivery, index) => (
              <div key={index} className="border rounded p-3 mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0">Delivery #{index + 1}</h6>
                  {splitDeliveryData.splits.length > 1 && (
                    <Button
                      color="outline-danger"
                      size="sm"
                      onClick={() => handleRemoveSplitDelivery(index)}
                      disabled={isSplitting}
                    >
                      <i className="ri-close-line"></i>
                    </Button>
                  )}
                </div>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label for={`quantity-${index}`}>Quantity</Label>
                      <Input
                        type="number"
                        id={`quantity-${index}`}
                        value={delivery.quantity}
                        onChange={(e) =>
                          handleSplitDeliveryChange(
                            index,
                            "quantity",
                            parseFloat(e.target.value) || 0
                          )
                        }
                        min={0}
                        max={poOrderedQuantity}
                        step="0.01"
                        disabled={isSplitting}
                      />
                      <div className="text-muted fs-12">
                        {item.measurement_name}
                      </div>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label for={`delivery-date-${index}`}>
                        Delivery Date
                      </Label>
                      <Input
                        type="date"
                        id={`delivery-date-${index}`}
                        value={delivery.delivery_date}
                        onChange={(e) =>
                          handleSplitDeliveryChange(
                            index,
                            "delivery_date",
                            e.target.value
                          )
                        }
                        disabled={isSplitting}
                      />
                    </FormGroup>
                  </Col>
                </Row>
              </div>
            ))}

            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <strong>Total Split Quantity: </strong>
                <span
                  className={
                    calculateTotalSplitQuantity() > poOrderedQuantity
                      ? "text-danger"
                      : "text-success"
                  }
                >
                  {calculateTotalSplitQuantity()} {item.measurement_name}
                </span>
                <div className="text-muted fs-12">
                  Remaining: {getRemainingQuantity()} {item.measurement_name}
                </div>
              </div>
              <Button
                color="outline-primary"
                size="sm"
                onClick={handleAddSplitDelivery}
                disabled={isSplitting || getRemainingQuantity() <= 0}
              >
                <i className="ri-add-line align-bottom me-1"></i>
                Add More
              </Button>
            </div>

            {calculateTotalSplitQuantity() > poOrderedQuantity && (
              <Alert color="danger" className="fs-13">
                <i className="ri-error-warning-line me-2"></i>
                Total split quantity exceeds ordered quantity. Please adjust the
                quantities.
              </Alert>
            )}

            {calculateTotalSplitQuantity() < poOrderedQuantity && (
              <Alert color="warning" className="fs-13">
                <i className="ri-alert-line me-2"></i>
                Total split quantity is less than ordered quantity. Please add
                more deliveries or adjust quantities.
              </Alert>
            )}
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setShowSplitDeliveryModal(false)}
            disabled={isSplitting}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleSplitDelivery}
            disabled={
              isSplitting ||
              calculateTotalSplitQuantity() !== poOrderedQuantity
            }
          >
            {isSplitting ? (
              <>
                <Spinner size="sm" className="me-2" />
                Splitting...
              </>
            ) : (
              "Split Delivery"
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

// Download Dropdown Component for Group
// Download Dropdown Component for Group
const GroupDownloadDropdown = ({
  purchaseOrderNumber,
  deliveryDate,
  items,
}: any) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [generateReportsPDFByUuid] = useGenerateReportsPDFByUuidMutation();
  const [getDeliveryRequestDownloadUids] =
    useGetDeliveryRequestDownloadUidsMutation();

  const { handleDeliveryDownload, isDownloading } = useDeliveryDownloader({
    getDeliveryRequestDownloadUids,
    generateReportsPDFByUuid,
  });

  const toggle = () => setDropdownOpen((prevState) => !prevState);

  // Calculate counts for different types of items
  const normalItems = items.filter(
    (item: any) =>
      !item.is_defect_items && getUpdatedDeliveryStatus(item) !== "delivered"
  );

  const defectItems = items.filter(
    (item: any) => item.is_defect_items || item.no_items_defected > 0
  );

  const redeliveryItems = items.filter(
    (item: any) => item.re_delivery_date || item.is_re_delivered
  );

  const handleDownload = async (type: string, isDefect: boolean = false) => {
    try {
      await handleDeliveryDownload(
        purchaseOrderNumber,
        deliveryDate,
        isDefect,
        type
      );
      setDropdownOpen(false); // Close dropdown after selection
    } catch (error) {
      console.error("Download error:", error);
      // Error handling is already done in the useDeliveryDownloader hook
    }
  };

  return (
    <Dropdown isOpen={dropdownOpen} toggle={toggle} direction="start">
      <DropdownToggle
        color="outline-success"
        size="sm"
        className="d-flex align-items-center"
        disabled={isDownloading}
      >
        {isDownloading ? (
          <Spinner size="sm" className="me-1" />
        ) : (
          <i className="ri-download-line align-bottom me-1"></i>
        )}
        Download
      </DropdownToggle>
      <DropdownMenu>
        <DropdownItem header>
          <i className="ri-download-cloud-line me-2"></i>
          Download Options
        </DropdownItem>
        <DropdownItem onClick={() => handleDownload("all", false)}>
          <div className="d-flex align-items-center">
            <i className="ri-file-list-line text-primary me-2"></i>
            <div>
              <div>All Items</div>
              <small className="text-muted">Complete delivery group</small>
            </div>
          </div>
        </DropdownItem>
        <DropdownItem divider />
        <DropdownItem onClick={() => handleDownload("normal", false)}>
          <div className="d-flex align-items-center">
            <i className="ri-checkbox-circle-line text-success me-2"></i>
            <div>
              <div>Normal Items</div>
              <small className="text-muted">
                {normalItems.length} items pending delivery
              </small>
            </div>
          </div>
        </DropdownItem>
        <DropdownItem onClick={() => handleDownload("defect", true)}>
          <div className="d-flex align-items-center">
            <i className="ri-error-warning-line text-danger me-2"></i>
            <div>
              <div>Defect Items</div>
              <small className="text-muted">
                {defectItems.length} items with defects
              </small>
            </div>
          </div>
        </DropdownItem>
        <DropdownItem onClick={() => handleDownload("redelivery", true)}>
          <div className="d-flex align-items-center">
            <i className="ri-refresh-line text-info me-2"></i>
            <div>
              <div>Redelivery Items</div>
              <small className="text-muted">
                {redeliveryItems.length} items for redelivery
              </small>
            </div>
          </div>
        </DropdownItem>
      </DropdownMenu>
    </Dropdown>
  );
};

// Delivery Date Group Component
const DeliveryDateGroup = ({
  date,
  items,
  isOpen,
  onToggle,
  refetchPurchaseOrder,
  supplierContact,
  purchaseOrderNumber,
}: any) => {
  const getDateStatus = (deliveryDate: string) => {
    const today = new Date();
    const delivery = new Date(deliveryDate);
    const timeDiff = delivery.getTime() - today.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

    if (daysDiff < 0)
      return {
        color: "danger",
        label: "Overdue",
        icon: "ri-alarm-warning-line",
      };
    if (daysDiff === 0)
      return { color: "warning", label: "Due Today", icon: "ri-time-line" };
    if (daysDiff <= 3)
      return {
        color: "info",
        label: "Due Soon",
        icon: "ri-calendar-event-line",
      };
    return { color: "success", label: "Upcoming", icon: "ri-calendar-line" };
  };

  const dateStatus = getDateStatus(date);
  const parsedDate = new Date(date);
  const formattedDate =
    Number.isNaN(parsedDate.getTime()) || date === "No Date Specified"
      ? "No Date Specified"
      : parsedDate.toLocaleDateString("en-US", {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        });

  const [generateReportsPDFByUuid] = useGenerateReportsPDFByUuidMutation();
  const [getDeliveryRequestDownloadUids] =
    useGetDeliveryRequestDownloadUidsMutation();

  const {
    isOpen: isModalOpen,
    status,
    permitNo,
    errorMessage,
    message,
    handleDeliveryDownload,
    isDownloading,
    closeDownloadModal,
  } = useDeliveryDownloader({
    getDeliveryRequestDownloadUids,
    generateReportsPDFByUuid,
  });

  // Whether this whole date-group is cancelled (item-level or all splits cancelled).
  // When true, "Download All" must produce the CANCELLED PDF, not the old one.
  const isItemFullyCancelled = (item: any) => {
    const splits = Array.isArray(item?.split_deliveries) ? item.split_deliveries : [];
    if (splits.length > 0) {
      return splits.every((s: any) => s?.delivery_status === "cancelled");
    }
    return item?.delivery_status === "cancelled";
  };
  const isGroupCancelled =
    items.length > 0 && items.every((item: any) => isItemFullyCancelled(item));

  const pendingItems = items.filter(
    (item: any) => !item.delivery_status || item.delivery_status === "pending"
  );
  const deliveredItems = items.filter(
    (item: any) => getUpdatedDeliveryStatus(item) === "delivered"
  );
  const defectedItems = items.filter(
    (item: any) =>
      getUpdatedDeliveryStatus(item) === "partially_delivered" &&
      item.no_items_defected > 0
  );

  // Generate badge with PO number and date
  const groupBadge = `${purchaseOrderNumber}-${formatDateForBadge(date)}`;

  return (
    <Card className="mb-3">
      <CardBody className="p-0">
        <div
          className="p-3 border-bottom cursor-pointer"
          onClick={onToggle}
          style={{ cursor: "pointer" }}
        >
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <i
                className={`${dateStatus.icon} text-${dateStatus.color} fs-18 me-3`}
              ></i>
              <div>
                <h6 className="mb-1 fw-semibold">{formattedDate}</h6>
                <div className="d-flex align-items-center gap-3">
                  <Badge color={dateStatus.color} className="fs-11">
                    {dateStatus.label}
                  </Badge>
                  <Badge color="primary" className="fs-11">
                    {groupBadge}
                  </Badge>
                  <span className="text-muted fs-12">
                    {items.length} item{items.length !== 1 ? "s" : ""} •
                    {pendingItems.length} Pending •{deliveredItems.length}{" "}
                    Recivied •{defectedItems.length} Defected
                  </span>
                </div>
              </div>
            </div>
            <div className="d-flex align-items-center gap-2">
              {/* Download Dropdown Button */}
              {/* <GroupDownloadDropdown
                purchaseOrderNumber={purchaseOrderNumber}
                deliveryDate={date}
                items={items}
              /> */}
              <i
                className={`ri-arrow-${isOpen ? "up" : "down"
                  }-s-line fs-18 text-muted`}
              ></i>
            </div>
          </div>
        </div>

        <Collapse isOpen={isOpen}>
          <div className="p-0">
            {/* Group Actions Bar */}
            <div className="bg-light p-3 border-bottom">
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted fs-13">Group Actions:</span>
                  {IsValidPermissions(["DOWNLOAD_PURCHASE_DELIVERY"]) &&
                    <Button
                      color="outline-success"
                      size="sm"
                      onClick={() =>
                        handleDeliveryDownload(
                          purchaseOrderNumber,
                          date,
                          false,
                          "all",
                          null,
                          null,
                          isGroupCancelled
                        )
                      }
                      disabled={isDownloading}
                    >
                      {isDownloading ? (
                        <Spinner size="sm" className="me-1" />
                      ) : (
                        <i className="ri-download-line align-bottom me-1"></i>
                      )}
                      Download All
                    </Button>}
                  {/* {IsValidPermissions(["DOWNLOAD_PURCHASE_DELIVERY"]) &&
                    <Button
                      color="outline-danger"
                      size="sm"
                      onClick={() =>
                        handleDeliveryDownload(
                          purchaseOrderNumber,
                          date,
                          true,
                          "defect"
                        )
                      }
                      disabled={isDownloading}
                    >
                      {isDownloading ? (
                        <Spinner size="sm" className="me-1" />
                      ) : (
                        <i className="ri-error-warning-line align-bottom me-1"></i>
                      )}
                      Download Defects
                    </Button>
                  } */}
                </div>
                <div className="text-muted fs-12">Group ID: {groupBadge}</div>
              </div>
            </div>

            {/* Responsive Table Container */}
            <div className="table-responsive">
              <Table className="table table-nowrap align-middle table-borderless mb-0">
                <thead className="table-light">
                  <tr>
                    <th scope="col" className="text-center">
                      Delivery planning
                    </th>
                    <th scope="col" className="ps-4">
                      Product Details
                    </th>
                    <th scope="col" className="text-center">
                      Ordered Qty
                    </th>
                    <th scope="col" className="text-center">
                      Recivied Qty
                    </th>
                    <th scope="col" className="text-center">
                      Accepted Qty
                    </th>
                    <th scope="col" className="text-center">
                      Defects
                    </th>
                    <th scope="col" className="text-center">
                      Expected Date
                    </th>
                    <th scope="col" className="text-center">
                      Status
                    </th>
                    <th scope="col" className="text-center">
                      Recivied Date
                    </th>
                    <th scope="col" className="text-center">
                      Contact Phone
                    </th>
                    <th scope="col" className="text-center pe-4">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items?.map((item: any, index: number) => (
                    <React.Fragment key={item.id}>
                      <DeliveryItem
                        item={item}
                        onUpdateItem={refetchPurchaseOrder}
                        supplierContact={supplierContact}
                        purchaseOrderNumber={purchaseOrderNumber}
                        groupDate={date}
                      />
                    </React.Fragment>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        </Collapse>
      </CardBody>
    </Card>
  );
};

// Main Delivery Component
const DeliveryComponent = ({ purchaseOrder, refetchPurchaseOrder }: any) => {
  const [openGroups, setOpenGroups] = useState<{ [key: string]: boolean }>({});
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [isBulkPlanningUpdating, setIsBulkPlanningUpdating] = useState(false);
  const [showStockTransferModal, setShowStockTransferModal] = useState(false);
  const [selectedStockAllocation, setSelectedStockAllocation] = useState<any>(null);
  const [stockTransferData, setStockTransferData] = useState<{
    transfer_status: "in_transit" | "received" | "partial_received" | "cancelled";
    quantity: number;
    delivery_person_contact: string;
    delivery_date: string;
    items_arrive_from: string;
  }>({
    transfer_status: "in_transit",
    quantity: 0,
    delivery_person_contact: "",
    delivery_date: "",
    items_arrive_from: "",
  });
  const [stockTransferError, setStockTransferError] = useState<string | null>(
    null
  );
  const [bulkUpdatePurchaseOrderDeliveryType] =
    useBulkUpdatePurchaseOrderDeliveryTypeMutation();
  const [updateStockAllocationTransferStatus, { isLoading: isUpdatingStockTransfer }] =
    useUpdateStockAllocationTransferStatusMutation();
  const [updateStockAllocationDeliveryDetails, { isLoading: isSavingDeliveryDetails }] =
    useUpdateStockAllocationDeliveryDetailsMutation();

  // Step 1 (delivery details) modal state for stock transfers
  const [showDeliveryDetailsModal, setShowDeliveryDetailsModal] = useState(false);
  const [deliveryDetailsAllocation, setDeliveryDetailsAllocation] = useState<any>(null);
  const [deliveryDetailsData, setDeliveryDetailsData] = useState({
    delivery_person_contact: "",
    delivery_date: "",
    items_arrive_from: "",
  });
  const [deliveryDetailsFiles, setDeliveryDetailsFiles] = useState<File[]>([]);
  const [deliveryDetailsError, setDeliveryDetailsError] = useState<string | null>(
    null
  );
  // Attachment files for the status-update modal
  const [stockStatusFiles, setStockStatusFiles] = useState<File[]>([]);

  const hasDeliveryDetails = (alloc: any) =>
    !!(
      alloc?.delivery_person_contact &&
      alloc?.delivery_date &&
      alloc?.items_arrive_from
    );

  const openDeliveryDetailsModal = (allocation: any) => {
    setDeliveryDetailsAllocation(allocation);
    setDeliveryDetailsData({
      delivery_person_contact: allocation?.delivery_person_contact || "",
      delivery_date: allocation?.delivery_date || "",
      items_arrive_from: allocation?.items_arrive_from || "",
    });
    setDeliveryDetailsFiles([]);
    setDeliveryDetailsError(null);
    setShowDeliveryDetailsModal(true);
  };

  const handleSubmitDeliveryDetails = async () => {
    if (!deliveryDetailsAllocation) return;
    if (
      !deliveryDetailsData.delivery_person_contact.trim() ||
      !deliveryDetailsData.delivery_date ||
      !deliveryDetailsData.items_arrive_from.trim()
    ) {
      setDeliveryDetailsError(
        "Delivery person contact, delivery date and items arrive from are all required."
      );
      return;
    }
    setDeliveryDetailsError(null);
    try {
      await updateStockAllocationDeliveryDetails({
        id: deliveryDetailsAllocation.id,
        values: {
          delivery_person_contact:
            deliveryDetailsData.delivery_person_contact.trim(),
          delivery_date: deliveryDetailsData.delivery_date,
          items_arrive_from: deliveryDetailsData.items_arrive_from.trim(),
        },
        attachments: deliveryDetailsFiles,
      }).unwrap();
      setShowDeliveryDetailsModal(false);
      setDeliveryDetailsAllocation(null);
      if (typeof refetchPurchaseOrder === "function") refetchPurchaseOrder();
    } catch (error: any) {
      setDeliveryDetailsError(
        error?.data?.error || "Failed to save delivery details."
      );
    }
  };

  // Mock supplier contact data - you should replace this with actual supplier data from your API
  const supplierContact = {
    name: purchaseOrder?.supplier_name || "NA",
    phone: purchaseOrder?.supplier_phone || "NA",
    email: purchaseOrder?.supplier_email || "NA",
  };

  // Get purchase order number
  const purchaseOrderNumber =
    purchaseOrder?.inventory_purchase_order_number || "PO_Unknown";

  // Group and sort items by expected_delivery_date
  const groupedItems = useMemo(() => {
    if (!purchaseOrder?.items) return {};

    const grouped: { [key: string]: any[] } = {};

    purchaseOrder.items.forEach((item: any) => {
      if (item?.is_split && Array.isArray(item?.split_deliveries) && item.split_deliveries.length) {
        const uniqueSplitDates: string[] = Array.from(
          new Set(
            item.split_deliveries
              .map((s: any) =>
                normalizeDeliveryDateValue(
                  s?.delivery_date || s?.po_expected_delivery_date
                ) || "No Date Specified"
              )
          )
        ) as string[];

        uniqueSplitDates.forEach((deliveryDate) => {
          if (!grouped[deliveryDate]) {
            grouped[deliveryDate] = [];
          }
          grouped[deliveryDate].push(item);
        });
        return;
      }

      const deliveryDate =
        normalizeDeliveryDateValue(item.po_expected_delivery_date) ||
        "No Date Specified";
      if (!grouped[deliveryDate]) {
        grouped[deliveryDate] = [];
      }
      grouped[deliveryDate].push(item);
    });

    // Sort the groups by date
    const sortedGroups: { [key: string]: any[] } = {};
    Object.keys(grouped)
      .sort((a, b) => {
        if (a === "No Date Specified") return 1;
        if (b === "No Date Specified") return -1;
        return new Date(a).getTime() - new Date(b).getTime();
      })
      .forEach((key) => {
        sortedGroups[key] = grouped[key];
      });

    return sortedGroups;
  }, [purchaseOrder?.items]);

  // Toggle group collapse
  const toggleGroup = (date: string) => {
    setOpenGroups((prev) => ({
      ...prev,
      [date]: !prev[date],
    }));
  };

  // Calculate summary statistics
  const allItemsDelivered = purchaseOrder?.items?.every(
    (item: any) => getUpdatedDeliveryStatus(item) === "delivered"
  );

  const deliveredItems = purchaseOrder?.items?.filter(
    (item: any) => getUpdatedDeliveryStatus(item) === "delivered"
  );

  const pendingItems = purchaseOrder?.items?.filter(
    (item: any) => !item.delivery_status || item.delivery_status === "pending"
  );

  // Feature 2: items that can be transferred to a new PO (still-open balance)
  const transferableItems = (purchaseOrder?.items || []).filter((item: any) => {
    const ordered = Number(item?.ordered_quantity ?? item?.quantity ?? 0);
    const delivered = Number(item?.delivered_quantity ?? 0);
    return (
      ["pending", "partially_delivered"].includes(item?.delivery_status) &&
      !item?.item_undeliverable &&
      ordered - delivered > 0
    );
  });

  const defectedItems = purchaseOrder?.items?.filter(
    (item: any) =>
      getUpdatedDeliveryStatus(item) === "partially_delivered" &&
      item.no_items_defected > 0
  );

  const planningTargets = useMemo(() => {
    const itemTargets = (purchaseOrder?.items || [])
      .filter(
        (item: any) =>
          !item.item_undeliverable && getUpdatedDeliveryStatus(item) !== "delivered"
      )
      .map((item: any) => ({
        id: item.id as number,
        type: "item" as const,
        planned: (item.delivery_type || "in_preparation") === "planned",
        purchaseRequest: item.purchase_request,
      }));

    const splitTargets = (purchaseOrder?.items || []).flatMap((item: any) =>
      (item.split_deliveries || [])
        .filter((split: any) => getUpdatedDeliveryStatus(split) !== "delivered")
        .map((split: any) => ({
          id: split.id as number,
          type: "split" as const,
          planned: (split.delivery_type || "in_preparation") === "planned",
        }))
    );

    return [...itemTargets, ...splitTargets];
  }, [purchaseOrder?.items]);

  const stockDeliveryRows = useMemo(() => {
    const rows: any[] = [];
    (purchaseOrder?.items || []).forEach((item: any) => {
      const allocations = Array.isArray(item?.stock_allocations)
        ? item.stock_allocations
        : [];
      allocations.forEach((allocation: any) => {
        rows.push({
          item_id: item.id,
          product_name: item.product_name,
          ordered_quantity: getPoSupplierQuantity(item),
          ...allocation,
        });
      });
    });
    return rows;
  }, [purchaseOrder?.items]);

  const getStockTransferMaxQuantity = (allocation: any) => {
    const issuedQty = parseFloat(String(allocation?.issued_qty ?? ""));
    if (!Number.isNaN(issuedQty) && issuedQty > 0) {
      return issuedQty;
    }
    return parseFloat(String(allocation?.allocated_from_stock_qty ?? 0)) || 0;
  };

  const openStockTransferModal = (allocation: any) => {
    const currentStatus = String(allocation?.transfer_status || "").toLowerCase();
    const defaultStatus =
      currentStatus === "in_transit" || currentStatus === "partial_received"
        ? "received"
        : "in_transit";
    const defaultQty = getStockTransferMaxQuantity(allocation);
    setSelectedStockAllocation(allocation);
    setStockTransferError(null);
    setStockStatusFiles([]);
    setStockTransferData({
      transfer_status: defaultStatus as
        | "in_transit"
        | "received"
        | "partial_received"
        | "cancelled",
      quantity: defaultQty,
      delivery_person_contact: allocation?.delivery_person_contact || "",
      delivery_date: allocation?.delivery_date || "",
      items_arrive_from: allocation?.items_arrive_from || "",
    });
    setShowStockTransferModal(true);
  };

  const handleSubmitStockTransferUpdate = async () => {
    if (!selectedStockAllocation) return;
    const nextStatus = stockTransferData.transfer_status;
    const maxQty = getStockTransferMaxQuantity(selectedStockAllocation);
    const qty = Math.min(
      Math.max(0, parseFloat(String(stockTransferData.quantity || 0)) || 0),
      maxQty
    );
    const isReceiveType =
      nextStatus === "received" || nextStatus === "partial_received";
    setStockTransferError(null);

    try {
      await updateStockAllocationTransferStatus({
        id: selectedStockAllocation.id,
        values: {
          transfer_status: nextStatus,
          ...(isReceiveType ? { quantity: qty } : {}),
        },
        attachments: stockStatusFiles,
      }).unwrap();
      setShowStockTransferModal(false);
      setSelectedStockAllocation(null);
      if (typeof refetchPurchaseOrder === "function") {
        refetchPurchaseOrder();
      }
    } catch (error: any) {
      const msg =
        error?.data?.error || "Failed to update stock transfer status.";
      setStockTransferError(msg);
      console.error("Failed to update stock transfer status", error);
    }
  };

  const allPlanningChecked =
    planningTargets.length > 0 && planningTargets.every((t) => t.planned);

  const handleBulkPlanningToggle = async (checked: boolean) => {
    if (!planningTargets.length) return;
    try {
      setIsBulkPlanningUpdating(true);
      const nextType = checked ? "planned" : "in_preparation";
      await bulkUpdatePurchaseOrderDeliveryType({
        delivery_type: nextType,
        item_ids: planningTargets
          .filter((target) => target.type === "item")
          .map((target) => target.id),
        split_ids: planningTargets
          .filter((target) => target.type === "split")
          .map((target) => target.id),
      }).unwrap();
      if (typeof refetchPurchaseOrder === "function") {
        refetchPurchaseOrder();
      }
    } catch (error) {
      console.error("Failed to update delivery planning in bulk", error);
    } finally {
      setIsBulkPlanningUpdating(false);
    }
  };

  // Expand all groups
  const expandAll = () => {
    const allOpen: { [key: string]: boolean } = {};
    Object.keys(groupedItems).forEach((date) => {
      allOpen[date] = true;
    });
    setOpenGroups(allOpen);
  };

  // Collapse all groups
  const collapseAll = () => {
    setOpenGroups({});
  };

  return (
    <Row>
      <Col xl={12} lg={12}>
        <Card>
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h5 className="card-title mb-0">Delivery Management</h5>
              <div className="d-flex align-items-center gap-2">
                {allItemsDelivered && (
                  <Badge color="success" className="fs-12">
                    <i className="ri-check-double-line align-middle me-1"></i>
                    All Items Delivered
                  </Badge>
                )}
                {purchaseOrder?.po_status !== "cancelled" &&
                  transferableItems.length > 0 && (
                    <Button
                      color="primary"
                      size="sm"
                      onClick={() => setShowTransferModal(true)}
                    >
                      <i className="ri-share-forward-line align-bottom me-1"></i>
                      Transfer to New PO
                    </Button>
                  )}
              </div>
            </div>

            {/* Group Controls */}
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <span className="text-muted fs-13">
                  Grouped by Expected Delivery Date •{" "}
                  {Object.keys(groupedItems).length} date group
                  {Object.keys(groupedItems).length !== 1 ? "s" : ""}
                </span>
              </div>
              <div className="d-flex gap-2 flex-wrap justify-content-end">
                <Button color="outline-primary" size="sm" onClick={expandAll}>
                  <i className="ri-expand-left-right-line align-bottom me-1"></i>
                  Expand All
                </Button>
                <Button
                  color="outline-secondary"
                  size="sm"
                  onClick={collapseAll}
                >
                  <i className="ri-collapse-left-right-line align-bottom me-1"></i>
                  Collapse All
                </Button>
              </div>
            </div>

            <div className="d-flex align-items-center gap-3 mb-3">
              <span className="text-muted fs-13">
                Checked = Planned | Unchecked = In preparation
              </span>
              <div className="d-flex align-items-center gap-2">
                <Input
                  type="checkbox"
                  className="form-check-input"
                  checked={allPlanningChecked}
                  disabled={planningTargets.length === 0 || isBulkPlanningUpdating}
                  onChange={(e) => handleBulkPlanningToggle(e.target.checked)}
                />
                <span className="text-muted fs-13">Select all</span>
                {isBulkPlanningUpdating && <Spinner size="sm" />}
              </div>
            </div>

            {/* Delivery Summary */}
            <Row className="mb-4">
              <Col lg={3} sm={6}>
                <Card className="card-animate">
                  <CardBody className="text-center">
                    <div className="d-flex align-items-center justify-content-center">
                      <div className="flex-shrink-0">
                        <i className="ri-inbox-line text-primary fs-24"></i>
                      </div>
                      <div className="flex-grow-1 ms-3">
                        <h4 className="mb-0">
                          {purchaseOrder?.items?.length || 0}
                        </h4>
                        <p className="text-muted mb-0 fs-12">Total Items</p>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
              <Col lg={3} sm={6}>
                <Card className="card-animate">
                  <CardBody className="text-center">
                    <div className="d-flex align-items-center justify-content-center">
                      <div className="flex-shrink-0">
                        <i className="ri-check-double-line text-success fs-24"></i>
                      </div>
                      <div className="flex-grow-1 ms-3">
                        <h4 className="mb-0">{deliveredItems?.length || 0}</h4>
                        <p className="text-muted mb-0 fs-12">Delivered</p>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
              <Col lg={3} sm={6}>
                <Card className="card-animate">
                  <CardBody className="text-center">
                    <div className="d-flex align-items-center justify-content-center">
                      <div className="flex-shrink-0">
                        <i className="ri-time-line text-warning fs-24"></i>
                      </div>
                      <div className="flex-grow-1 ms-3">
                        <h4 className="mb-0">{pendingItems?.length || 0}</h4>
                        <p className="text-muted mb-0 fs-12">Pending</p>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
              <Col lg={3} sm={6}>
                <Card className="card-animate">
                  <CardBody className="text-center">
                    <div className="d-flex align-items-center justify-content-center">
                      <div className="flex-shrink-0">
                        <i className="ri-error-warning-line text-danger fs-24"></i>
                      </div>
                      <div className="flex-grow-1 ms-3">
                        <h4 className="mb-0">{defectedItems?.length || 0}</h4>
                        <p className="text-muted mb-0 fs-12">Defected</p>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* Delivery Date Groups */}
            {stockDeliveryRows.length > 0 && (
              <Card className="border border-info-subtle mb-4">
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <h6 className="mb-1">Stock Transfer Delivery</h6>
                      <p className="text-muted mb-0 fs-12">
                        Separate transfer tracking for stock-allocated quantities.
                      </p>
                    </div>
                    <Badge color="info" className="fs-11">
                      {stockDeliveryRows.length} transfer
                      {stockDeliveryRows.length !== 1 ? "s" : ""}
                    </Badge>
                  </div>
                  <div className="table-responsive">
                    <Table className="table align-middle table-nowrap mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Product</th>
                          <th>Allocated Qty</th>
                          <th>Reference</th>
                          <th>Status</th>
                          <th>Delivery Details</th>
                          <th>Attachments</th>
                          <th className="text-end">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stockDeliveryRows.map((alloc: any) => {
                          const status = String(alloc.transfer_status || "").toLowerCase();
                          return (
                            <tr key={`stock-transfer-${alloc.id}`}>
                              <td>
                                <div className="fw-semibold">{alloc.product_name || "-"}</div>
                              </td>
                              <td>{alloc.allocated_from_stock_qty || 0}</td>
                              <td>
                                {alloc.source_stock_reference ||
                                  alloc.allocation_no ||
                                  `ALLOC-${alloc.id}`}
                              </td>
                              <td>
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                  <Badge
                                    color={
                                      status === "received"
                                        ? "success"
                                        : status === "partial_received"
                                        ? "warning"
                                        : status === "cancelled"
                                        ? "danger"
                                        : "primary"
                                    }
                                  >
                                    {alloc.transfer_status}
                                  </Badge>
                                  {status === "cancelled" &&
                                    String(alloc.notes || "").toLowerCase().includes("po") &&
                                    String(alloc.notes || "").toLowerCase().includes("rejected") && (
                                      <Badge color="warning">
                                        Released due to PO rejection
                                      </Badge>
                                    )}
                                </div>
                              </td>
                              <td>
                                {alloc.delivery_person_contact ||
                                alloc.delivery_date ||
                                alloc.items_arrive_from ? (
                                  <div className="small">
                                    {alloc.delivery_person_contact && (
                                      <div>
                                        <i className="ri-user-line align-middle me-1"></i>
                                        {alloc.delivery_person_contact}
                                      </div>
                                    )}
                                    {alloc.delivery_date && (
                                      <div className="text-muted">
                                        <i className="ri-calendar-line align-middle me-1"></i>
                                        {alloc.delivery_date}
                                      </div>
                                    )}
                                    {alloc.items_arrive_from && (
                                      <div className="text-muted">
                                        <i className="ri-map-pin-line align-middle me-1"></i>
                                        {alloc.items_arrive_from}
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-muted">—</span>
                                )}
                              </td>
                              <td>
                                {Array.isArray(alloc.attachments) &&
                                alloc.attachments.length > 0 ? (
                                  <div className="d-flex flex-column gap-1">
                                    {alloc.attachments.map((att: any) => (
                                      <a
                                        key={att.id}
                                        href={att.file_url || "#"}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-truncate small"
                                        style={{ maxWidth: 160 }}
                                        title={att.name || `Attachment ${att.id}`}
                                      >
                                        <i className="ri-attachment-2 align-middle me-1"></i>
                                        {att.name || `File ${att.id}`}
                                      </a>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-muted">—</span>
                                )}
                              </td>
                              <td className="text-end">
                                {!["received", "cancelled"].includes(status) && (
                                  <div className="d-flex gap-2 justify-content-end flex-wrap">
                                    <Button
                                      size="sm"
                                      color="outline-secondary"
                                      disabled={isSavingDeliveryDetails}
                                      onClick={() =>
                                        openDeliveryDetailsModal(alloc)
                                      }
                                    >
                                      <i className="ri-truck-line align-middle me-1"></i>
                                      Update
                                    </Button>
                                    {hasDeliveryDetails(alloc) && (
                                      <Button
                                        size="sm"
                                        color="outline-primary"
                                        disabled={isUpdatingStockTransfer}
                                        onClick={() =>
                                          openStockTransferModal(alloc)
                                        }
                                      >
                                        Update Status
                                      </Button>
                                    )}
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            )}
            <div className="delivery-groups">
              {Object.keys(groupedItems).length > 0 ? (
                Object.keys(groupedItems).map((date) => (
                  <DeliveryDateGroup
                    key={date}
                    date={date}
                    items={groupedItems[date]}
                    isOpen={openGroups[date] || false}
                    onToggle={() => toggleGroup(date)}
                    refetchPurchaseOrder={refetchPurchaseOrder}
                    supplierContact={supplierContact}
                    purchaseOrderNumber={purchaseOrderNumber}
                  />
                ))
              ) : (
                <div className="text-center py-5">
                  <i className="ri-inbox-line display-4 text-muted opacity-50"></i>
                  <h5 className="mt-3 text-muted">No Items Found</h5>
                  <p className="text-muted mb-4">
                    There are no items in this purchase order.
                  </p>
                </div>
              )}
            </div>
          </CardBody>
        </Card>
      </Col>
      <Modal
        isOpen={showStockTransferModal}
        toggle={() =>
          !isUpdatingStockTransfer && setShowStockTransferModal(false)
        }
        size="md"
      >
        <ModalHeader
          toggle={() =>
            !isUpdatingStockTransfer && setShowStockTransferModal(false)
          }
        >
          Update Stock Transfer Status
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label>Current Status</Label>
              <div>
                <Badge color="info">
                  {selectedStockAllocation?.transfer_status || "-"}
                </Badge>
              </div>
            </FormGroup>
            <FormGroup>
              <Label for="globalStockTransferStatus">Next Status</Label>
              <Input
                id="globalStockTransferStatus"
                type="select"
                value={stockTransferData.transfer_status}
                onChange={(e) =>
                  setStockTransferData((prev) => ({
                    ...prev,
                    transfer_status: e.target.value as
                      | "in_transit"
                      | "received"
                      | "partial_received"
                      | "cancelled",
                  }))
                }
                disabled={isUpdatingStockTransfer}
              >
                <option value="in_transit">In Transit</option>
                <option value="received">Received</option>
                <option value="partial_received">Partial Received</option>
                <option value="cancelled">Cancelled</option>
              </Input>
            </FormGroup>
            {(stockTransferData.transfer_status === "received" ||
              stockTransferData.transfer_status === "partial_received") && (
              <FormGroup>
                <Label for="globalStockReceivedQty">Received Quantity</Label>
                <Input
                  id="globalStockReceivedQty"
                  type="number"
                  min={0}
                  max={getStockTransferMaxQuantity(selectedStockAllocation)}
                  step="0.01"
                  value={stockTransferData.quantity}
                  onChange={(e) =>
                    setStockTransferData((prev) => ({
                      ...prev,
                      quantity: Math.min(
                        Math.max(0, parseFloat(e.target.value || "0") || 0),
                        getStockTransferMaxQuantity(selectedStockAllocation)
                      ),
                    }))
                  }
                  disabled={isUpdatingStockTransfer}
                />
                <div className="text-muted fs-12 mt-1">
                  Max:{" "}
                  {getStockTransferMaxQuantity(selectedStockAllocation).toFixed(2)}
                </div>
              </FormGroup>
            )}

            {/* Saved delivery details summary (captured in the earlier "Update" step) */}
            {selectedStockAllocation?.delivery_person_contact && (
              <div className="border rounded p-2 mb-3 bg-light small">
                <div className="fw-semibold mb-1">Delivery Details</div>
                <div>Contact: {selectedStockAllocation.delivery_person_contact}</div>
                <div>Date: {selectedStockAllocation.delivery_date || "-"}</div>
                <div>
                  Arrives from: {selectedStockAllocation.items_arrive_from || "-"}
                </div>
              </div>
            )}

            <FormGroup>
              <Label for="globalStockTransferAttachments">
                Attachments (optional)
              </Label>
              <Input
                id="globalStockTransferAttachments"
                type="file"
                multiple
                disabled={isUpdatingStockTransfer}
                onChange={(e) =>
                  setStockStatusFiles(Array.from(e.target.files || []))
                }
              />
              {stockStatusFiles.length > 0 && (
                <ul className="mt-2 mb-0 ps-3 small">
                  {stockStatusFiles.map((f, idx) => (
                    <li key={idx} className="text-truncate">
                      {f.name}
                    </li>
                  ))}
                </ul>
              )}
            </FormGroup>

            {stockTransferError && (
              <div className="alert alert-danger py-2 mb-0">
                {stockTransferError}
              </div>
            )}
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setShowStockTransferModal(false)}
            disabled={isUpdatingStockTransfer}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleSubmitStockTransferUpdate}
            disabled={isUpdatingStockTransfer}
          >
            {isUpdatingStockTransfer ? (
              <>
                <Spinner size="sm" className="me-2" />
                Updating...
              </>
            ) : (
              "Update Status"
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Step 1: Delivery Details modal (opened by the "Update" button) */}
      <Modal
        isOpen={showDeliveryDetailsModal}
        toggle={() =>
          !isSavingDeliveryDetails && setShowDeliveryDetailsModal(false)
        }
        centered
      >
        <ModalHeader
          toggle={() =>
            !isSavingDeliveryDetails && setShowDeliveryDetailsModal(false)
          }
        >
          Update Delivery Details
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label for="ddContact">
                Delivery Person Contact <span className="text-danger">*</span>
              </Label>
              <Input
                id="ddContact"
                type="text"
                value={deliveryDetailsData.delivery_person_contact}
                onChange={(e) =>
                  setDeliveryDetailsData((prev) => ({
                    ...prev,
                    delivery_person_contact: e.target.value,
                  }))
                }
                disabled={isSavingDeliveryDetails}
                placeholder="Name / phone of the delivery person"
              />
            </FormGroup>
            <FormGroup>
              <Label for="ddDate">
                Delivery Date <span className="text-danger">*</span>
              </Label>
              <Input
                id="ddDate"
                type="date"
                value={deliveryDetailsData.delivery_date}
                onChange={(e) =>
                  setDeliveryDetailsData((prev) => ({
                    ...prev,
                    delivery_date: e.target.value,
                  }))
                }
                disabled={isSavingDeliveryDetails}
              />
            </FormGroup>
            <FormGroup>
              <Label for="ddArriveFrom">
                Items Arrive From <span className="text-danger">*</span>
              </Label>
              <Input
                id="ddArriveFrom"
                type="text"
                value={deliveryDetailsData.items_arrive_from}
                onChange={(e) =>
                  setDeliveryDetailsData((prev) => ({
                    ...prev,
                    items_arrive_from: e.target.value,
                  }))
                }
                disabled={isSavingDeliveryDetails}
                placeholder="Where the items are arriving from"
              />
            </FormGroup>
            <FormGroup>
              <Label for="ddFiles">Attachments (optional)</Label>
              <Input
                id="ddFiles"
                type="file"
                multiple
                disabled={isSavingDeliveryDetails}
                onChange={(e) =>
                  setDeliveryDetailsFiles(Array.from(e.target.files || []))
                }
              />
              {deliveryDetailsFiles.length > 0 && (
                <ul className="mt-2 mb-0 ps-3 small">
                  {deliveryDetailsFiles.map((f, idx) => (
                    <li key={idx} className="text-truncate">
                      {f.name}
                    </li>
                  ))}
                </ul>
              )}
            </FormGroup>
            {deliveryDetailsError && (
              <div className="alert alert-danger py-2 mb-0">
                {deliveryDetailsError}
              </div>
            )}
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setShowDeliveryDetailsModal(false)}
            disabled={isSavingDeliveryDetails}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleSubmitDeliveryDetails}
            disabled={isSavingDeliveryDetails}
          >
            {isSavingDeliveryDetails ? (
              <>
                <Spinner size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              "Save Details"
            )}
          </Button>
        </ModalFooter>
      </Modal>

      <TransferToNewPoModal
        isOpen={showTransferModal}
        toggle={() => setShowTransferModal(false)}
        purchaseOrder={purchaseOrder}
        onSuccess={() => refetchPurchaseOrder?.()}
      />
    </Row>
  );
};

export default DeliveryComponent;
